// Which car goes where, in what order, and what it costs.
//
// CommonJS so scripts/check-dispatch.js can require it with no transpiler, the
// same arrangement bookingRules.js uses. Pure: no Firestore, no Vue, no clock.
// Everything it knows about the world arrives in its arguments.
//
// Builds on geo (where), travelTime (how long), installTime (how long parked),
// chargers (when to plug in) and fleetOrder/registerCar (what is available).
//
// ---------------------------------------------------------------------------
// What it optimises, and why it is not what you would guess
// ---------------------------------------------------------------------------
//
// The objective is BAHT OF FUEL, not minutes. The five drivers are paid for the
// day whether the route is tight or slack, so their time is a fixed cost, not a
// marginal one. What actually varies with the plan is energy: ฿0.60/km on
// 4ฒฎ-7073 against ฿4.92/km on the เบนซิน spare, an eightfold spread.
//
// Time therefore enters as a CONSTRAINT rather than a cost -- windows must be
// met, the day must end -- plus a small per-minute tiebreaker so that two plans
// costing the same fuel resolve to the more compact one.
//
// The consequence is the opposite of the intuitive rule. Because insertion is
// priced at `ฮkm x car.costPerKm`, the cheap cars win the LONG routes and the
// expensive ones are left the short hops. That falls out of the arithmetic; it
// is not special-cased anywhere. It is also worth roughly a third of the daily
// fuel bill, so it is the single most valuable thing in this file.
//
// ---------------------------------------------------------------------------
// What it deliberately does NOT do
// ---------------------------------------------------------------------------
//
// No fixed-day zoning for the far southeast. That was proposed and then
// withdrawn on the evidence: over 517 working days from 2025-01-01, only 46
// (8.9%) carried a lone far-east job with no other southeast work to share the
// trip -- worth perhaps ฿2-3k of fuel a year, nowhere near enough to tell a
// customer in บางพลี which weekday they may have a delivery. บางพลี is in fact
// the 4th-busiest district in the history.
//
// Those days are not ignored, they are surfaced: a stop whose cost-per-job is
// far above the day's median comes back flagged in `route.stops[].flags`, so
// staff see the expensive run without a policy forbidding it.

const geo = require('./geo')
const travelTime = require('./travelTime')
const installTime = require('./installTime')
const chargers = require('./chargers')
const R = require('./bookingRules')
const V = require('./visitRules')
const DR = require('./deliveryRow')

// Slot windows, taken from bookingRules.SLOT_TIME rather than restated, so the
// promise the booking page makes to the customer and the window the planner
// honours cannot drift apart.
function slotWindow (slot) {
  const text = R.SLOT_TIME[slot] || R.SLOT_TIME.full
  const parts = String(text).split('-')
  return {
    from: travelTime.toMinutes(parts[0].trim()),
    to: travelTime.toMinutes(parts[1].trim())
  }
}

const DEFAULTS = {
  // The crew's working day, BUSINESS INPUT from the delivery team and HR on
  // 2026-09-29: 08:00-17:00, and the crew is in the office crewPrepMinutes
  // before the car leaves. Everything outside it is overtime at
  // overtimeCostPerMinute -- a 06:30 departure means a 06:00 start, two hours
  // before the day begins. A 06:00-15:00 shift is being negotiated with the
  // drivers (to be revisited around 2026-10-13); if it is agreed, it is these
  // two lines.
  workStart: '08:00',
  workEnd: '17:00',
  crewPrepMinutes: 30,

  // 08:30, because that is when most rounds actually leave: the crew arrives at
  // 08:00. It was 06:30 -- which arrives before an 08:00 window rather than an
  // hour into it, the depot being 40 km out -- but at these hours it is two
  // hours of overtime per car, and the planners now leave early only for a long
  // round, by setting that car's departure (departByCar) or the day's.
  departAt: '08:30',

  // The earliest the PLANNER may send a car without being asked -- which it
  // does only when the round pays for the overtime: a far เช้า job that 08:30
  // cannot reach, or a day that would otherwise need another car. This is the
  // planners' own practice (early only for a long round), made automatic, and
  // it is priced, not free: see simulate(). A departure typed for a car still
  // wins outright.
  earliestDepartAt: '06:30',

  // departAt is the EARLIEST a car may leave, not the time it does. Each round
  // is then held back to reach its first stop when that stop's window opens,
  // because a driver standing outside a shuttered shop at 07:26 for an 08:00
  // appointment is being paid for 34 minutes of nothing. See trimDeparture.
  //
  // The buffer is what is deliberately NOT taken. That idle time is also the
  // cushion that absorbs a bad morning on กาญจนาภิเษก, and a round trimmed to
  // the last minute turns every slip into a late first customer.
  arriveBufferMinutes: 10,

  // Stops staff moved by hand: { <job id>: <plate> } (ย้ายไปรถ on the page).
  // A pinned stop is placed only on its car and never moved off it by the
  // improvement passes; everything else is re-planned around it. If the car
  // cannot take it -- another change made that impossible since -- it is
  // placed wherever it fits and says so (stop.pin.held === false), because a
  // customer quietly stranded is worse than a hand move quietly undone.
  pins: {},

  // Off only to see a plan as routed, before departures are held back --
  // check-dispatch.js compares the two to prove trimming moves no customer.
  trimDepartures: true,

  // Per-car override, { '4ฒฎ-7073': '07:15' }, for what the planner cannot
  // know: a driver who must load first, a car wanted on the depot charger. Set
  // by staff, and it wins -- unless it makes the round infeasible, in which
  // case the computed time stands and the route says so.
  departByCar: {},

  // Flexible within a window, per the delivery team: the driver eats somewhere
  // between 11:00 and 14:00. Taken at the first opportunity inside it, which is
  // also what gives a charge its one chance to be free -- see the charge
  // placement in simulate().
  lunchMinutes: 30,
  lunchAfter: '11:00',
  lunchBefore: '14:00',

  // There is no separate "prefer to be back by": being back after workEnd is
  // overtime, and overtime is priced. (It was a guessed 16:00, and 14:30 before
  // that, which charged a large penalty against what the บ่าย slot sold to
  // 17:00 makes unavoidable.)

  // Hard. A route projected back at the depot after this is not a plan.
  mustReturnBy: '19:00',

  // An out-of-area route too long for one day sleeps on the road (delivery,
  // 2026-10-15): day 1 leaves 05:00-06:00 and its last drop ends by 18:00; the
  // night is at that drop, with a full charge or tank; day 2's first drop is at
  // 08:00 (the driver leaves as early as that leg needs) and the car is home by
  // mustReturnBy. Only planTrips / tripForCar read these.
  twoDayDepartAt: '06:00',
  twoDayEarliestDepartAt: '05:00',
  twoDayDay1EndBy: '18:00',
  twoDayDay2StartAt: '08:00',

  // DC charging tapers above ~80% SoC, so the linear 70km/30min rate is only
  // honest for the first charge of a day. Since the cars start full from the
  // depot, one is enough for every route in the service area.
  maxChargesPerDay: 1,

  // A charge is a LAST RESORT, not a saving. The delivery team's words: a
  // public charger sometimes has a queue, the half hour becomes an hour, and
  // the customer after it is missed. Their planners therefore charge as little
  // as possible, and the arithmetic agrees with them -- one charge buys ~70 km,
  // which on the EV instead of the cheapest NGV car saves about ฿70, while an
  // hour of a two-person crew costs ฿140.
  //
  // So planDay() first plans with no charging at all, and only when that
  // strands work does it plan again with charging allowed (see planDay). In
  // that second plan each charge is priced at chargePenalty -- the risk, in
  // baht -- so a charge is taken only where nothing cheaper fits. Below the
  // spare's 400 on purpose: charging an EV is preferred to bringing out the
  // ฿4.92/km standby car. BUSINESS INPUT.
  chargePenalty: 300,

  // Every charge is scheduled as the bad day, not the average one: the 30
  // minutes of charging plus this much queue. The stops after it are then
  // timed so that a queue makes the driver early, not the customer kept
  // waiting. On a day the charger is free, the driver arrives early and waits.
  chargeQueueMinutes: 30,

  // Refuelling an NGV or petrol car is not the same problem as charging an EV
  // and must not be modelled as one. A tank is refilled in minutes, to full,
  // at stations that are everywhere -- so there is no location table, no
  // detour, and no partial top-up.
  //
  // Treating a fuel car's tank range as a hard daily cap was a bug: it made
  // 1ฒบ-7127 (180 km) unable to accept a 190 km day that any driver would
  // simply refuel in the middle of, and pushed that work onto 3ฒฌ-5175 -- the
  // ฿4.92 spare -- purely because its 600 km tank was the only one "big
  // enough". The planner was buying the most expensive fuel in the fleet to
  // avoid a fifteen-minute stop.
  refuelMinutes: 15,
  maxRefuelsPerDay: 2,

  // Road distance here is haversine x 1.35, worth about +/-15%.
  reserveFraction: chargers.RESERVE_FRACTION,

  // Baht per minute. Small on purpose: driver time is a fixed cost, so this is
  // only a tiebreaker between plans of equal fuel.
  minuteCost: 0.5,

  // Baht per minute outside workStart-workEnd. BUSINESS INPUT, given by the delivery
  // team on 2026-09-29: ฿70 an hour per person, and every car carries two (the
  // driver and the assistant), so ฿140 an hour. It was 3 before that figure
  // existed -- a guess that overstated it. Raise it and the planner will trade
  // fuel for an earlier finish; check-dispatch.js asserts fuel stays a
  // meaningful share so that a change here cannot quietly turn this into a
  // different optimiser.
  overtimeCostPerMinute: 70 * 2 / 60,

  // The spare is the most expensive vehicle in the fleet by a factor of eight.
  // Using it should take a deliberately large amount of saving to justify.
  sparePenalty: 400,

  // Rounds of local improvement after the initial build.
  improvePasses: 40,

  // Then this many rounds of the wider search (searchWider): take a few
  // nearby stops out, put them back where cheapest, keep it if the day got
  // cheaper. A count, not a time limit, so the same day always plans the same.
  searchRounds: 200,
  searchSeed: 20260929,
  // Baht a round may be worse than the day it came from and still be kept,
  // at the start of the search; it shrinks to nothing by the last round.
  searchSlack: 60,
  // The share of rounds that try the taken stops together on an idle car
  // (see searchWider): the only way the search sends one more car out.
  searchOpenCar: 0.3,
  // How many times planOptions plans each of its two plans, from different
  // seeds, keeping the cheapest. Measured 2026-09-29, fuel + overtime:
  //   runs      1      3      6      12
  //   29 Sep  ฿861   ฿789   ฿789   ฿731
  //   24 Sep  ฿1998  ฿1837  ฿1837  ฿1781
  // Each run of both plans costs ~0.8 s in the browser's worker, so four
  // takes most of the gain for about three seconds of วางแผน.
  planRuns: 4,

  // A stop costing this many times the day's median is flagged for staff.
  expensiveStopFactor: 3,

  // How far a dropped pin may sit from the ตำบล the booking claims before the
  // two are treated as disagreeing.
  //
  // This matters because the planner resolves a booking through its PIN first,
  // while every screen shows the ตำบล -- so a mistyped coordinate routes a
  // driver somewhere the paperwork never mentions, and nothing says a word. A
  // ตำบล centroid is within about 3 km of anywhere inside it in town, so 5 km
  // is comfortably past honest imprecision and into contradiction.
  pinMismatchKm: 5
}

function config (overrides) {
  const c = Object.assign({}, DEFAULTS, overrides || {})
  c.departMinutes = travelTime.toMinutes(c.departAt)
  const earliest = travelTime.toMinutes(c.earliestDepartAt)
  c.earliestDepartMinutes = earliest == null ? c.departMinutes : Math.min(earliest, c.departMinutes)
  c.lunchAfterMinutes = travelTime.toMinutes(c.lunchAfter)
  c.lunchBeforeMinutes = travelTime.toMinutes(c.lunchBefore)
  c.workStartMinutes = travelTime.toMinutes(c.workStart)
  c.workEndMinutes = travelTime.toMinutes(c.workEnd)
  c.mustReturnMinutes = travelTime.toMinutes(c.mustReturnBy)
  c.twoDayDepartMinutes = travelTime.toMinutes(c.twoDayDepartAt)
  const twoEarliest = travelTime.toMinutes(c.twoDayEarliestDepartAt)
  c.twoDayEarliestDepartMinutes = twoEarliest == null ? c.twoDayDepartMinutes : Math.min(twoEarliest, c.twoDayDepartMinutes)
  c.twoDayDay1EndMinutes = travelTime.toMinutes(c.twoDayDay1EndBy)
  c.twoDayDay2StartMinutes = travelTime.toMinutes(c.twoDayDay2StartAt)
  // Unparseable overrides are dropped rather than defaulted: a car quietly
  // leaving at 06:30 because someone typed "7" is worse than one that ignores
  // the entry and says the computed time.
  c.departByCarMinutes = {}
  Object.keys(c.departByCar || {}).forEach(plate => {
    const m = travelTime.toMinutes(c.departByCar[plate])
    if (m != null) c.departByCarMinutes[plate] = m
  })
  return c
}

// A booking becomes a job: a point, a duration, and a window.
// Anything that cannot be placed is not planned -- it is returned with a reason,
// because a job silently dropped is the one failure mode staff cannot see.
function toJob (booking) {
  const point = geo.resolveBooking(booking)
  const window = slotWindow(booking.slot)

  // An explicit count beats one read out of รายการสินค้า, which beats the
  // default tier. The SOURCE travels with the number, because a duration read
  // out of free text is an estimate and the plan must be able to say so -- an
  // hour that was guessed and an hour that was counted look identical on a
  // timetable, and only one of them is worth trusting.
  const count = installTime.itemsForBooking(booking)
  const items = count.items

  // How long the stop takes, which is NOT always a function of the count: staff
  // can set the duration by hand when they know this model, or this customer,
  // or this loading dock. When they have, the count underneath stops mattering
  // to the timetable -- so the job reports itself as confident even if the count
  // was guessed, and the plan must not warn about a number nothing rests on.
  const duration = installTime.minutesForBooking(booking)

  // A pin that contradicts the ตำบล beside it is a data error worth seeing, not
  // a rounding difference. Measured, not judged, here -- the threshold lives in
  // the config so the operation can tune what counts as a contradiction.
  let pinMismatchKm = null
  if (point.precision === 'exact') {
    const stated = geo.resolveLocation(booking.location)
    if (stated.precision === 'subdistrict') {
      const apart = geo.haversineKm(point, stated)
      if (apart != null) pinMismatchKm = apart
    }
  }

  return {
    id: booking.id || booking.soNo || null,
    booking: booking,
    point: point,
    precision: point.precision,
    pinMismatchKm: pinMismatchKm,
    // A return-only pickup has no items to install; normalizeItems would read
    // its blank count as the default tier.
    items: duration.source === 'return' ? 0 : installTime.normalizeItems(items),
    itemSource: count.source,
    itemsConfident: duration.confident,
    installMinutes: duration.minutes,
    installSource: duration.source,
    returnMinutes: duration.returnMinutes || 0,
    slot: booking.slot || 'full',
    windowFrom: window.from,
    windowTo: window.to,
    bookings: [booking],
    memberMinutes: [duration.minutes],
    splitVisit: false
  }
}

// ---------------------------------------------------------------------------
// Several bookings, one destination
// ---------------------------------------------------------------------------
//
// A customer who orders twice has two SOs, and sales books both for the same
// day. Planned one job per booking, the two could go out on two trucks to one
// door -- it happened on 2026-10-08 -- while the driver app, which groups by
// phone, would have shown each driver half a visit.
//
// The key is phone AND ตำบล, not phone alone as driverDay.stopKeyFor has it: a
// branch switchboard or a shop's own number typed for a walk-in is shared by
// customers who have nothing to do with each other, and the ตำบล is what keeps
// them apart. No phone or no ตำบล means no merge -- a name is not an identity.
// See docs/superpowers/specs/2026-09-28-same-visit-design.md.
//
// The key is visitRules.visitKey, the one a person's link on bookingDate is
// checked against -- so the planner merges at least every visit someone linked,
// and also the pairs nobody got round to linking.
function visitKeyFor (booking) {
  return V.visitKey(booking)
}

// -> [[booking, ...], ...] in dispatch order. A booking joins the first cluster
// of its key whose window it overlaps; เช้า and บ่าย for one customer do not
// overlap, and are two visits the customer asked for, so both come back marked
// `split` for the plan to flag rather than one of them being quietly moved.
function groupVisits (bookings) {
  const clusters = []
  const byKey = {}
  ;(bookings || []).forEach(b => {
    const key = visitKeyFor(b)
    const w = slotWindow(b && b.slot)
    if (key) {
      const home = (byKey[key] || []).find(c => Math.min(c.to, w.to) > Math.max(c.from, w.from))
      if (home) {
        home.bookings.push(b)
        home.from = Math.max(home.from, w.from)
        home.to = Math.min(home.to, w.to)
        return
      }
    }
    const cluster = { key: key, bookings: [b], from: w.from, to: w.to, split: false }
    clusters.push(cluster)
    if (key) (byKey[key] = byKey[key] || []).push(cluster)
  })
  Object.keys(byKey).forEach(k => {
    if (byKey[k].length > 1) byKey[k].forEach(c => { c.split = true })
  })
  return clusters.map(c => Object.assign(c.bookings.slice(), { split: c.split }))
}

// Weakest first: a merged stop is only as certain as its least certain member.
const SOURCE_WEAKNESS = ['default', 'productList', 'explicit']
function weakest (sources) {
  for (let i = 0; i < SOURCE_WEAKNESS.length; i++) {
    if (sources.indexOf(SOURCE_WEAKNESS[i]) !== -1) return SOURCE_WEAKNESS[i]
  }
  return sources[0]
}

// One job for a cluster. A single booking is exactly toJob's answer.
//
// The duration is NOT the sum of the members' durations. The tiers carry a
// fixed cost per visit -- unloading, setting up, the walk-through -- so two
// 2-item SOs are one 4-item installation (60 minutes), not two visits of 45.
// A member whose duration staff set by hand keeps it, added on top: they set
// it for that work, and nothing here knows better.
function toVisitJob (cluster) {
  const members = cluster || []
  const jobs = members.map(toJob)
  const job = jobs.length === 1 ? jobs[0] : null
  if (job) { job.splitVisit = !!members.split; return job }

  const primary = jobs.find(j => j.precision === 'exact') || jobs[0]

  let tierItems = 0
  let manualMinutes = 0
  let returnMinutes = 0
  const autoSources = []
  let confident = true
  members.forEach((b, i) => {
    if (jobs[i].installSource === 'manual') {
      manualMinutes += jobs[i].installMinutes
    } else {
      tierItems += jobs[i].items
      returnMinutes += jobs[i].returnMinutes || 0
      autoSources.push(jobs[i].installSource)
      if (!jobs[i].itemsConfident) confident = false
    }
  })
  const minutes = installTime.clampMinutes(
    (tierItems ? installTime.installMinutes(tierItems) : 0) + manualMinutes + returnMinutes)

  return Object.assign({}, primary, {
    items: installTime.normalizeItems(jobs.reduce((a, j) => a + j.items, 0)),
    itemSource: weakest(jobs.map(j => j.itemSource)),
    itemsConfident: confident,
    installMinutes: minutes,
    installSource: autoSources.length ? weakest(autoSources) : 'manual',
    windowFrom: Math.max.apply(null, jobs.map(j => j.windowFrom)),
    windowTo: Math.min.apply(null, jobs.map(j => j.windowTo)),
    bookings: members.slice(),
    memberMinutes: jobs.map(j => j.installMinutes),
    splitVisit: !!members.split
  })
}

// A stop's minutes shared out over its bookings in proportion to what each
// would have taken alone, the remainder on the first, so the carBooking grid's
// total for a car is the length of the stop and not of separate visits.
function shareMinutes (total, weights) {
  const w = weights || []
  if (w.length <= 1) return [total]
  // Every weight is a clamped duration, so at least 15 and the sum never 0.
  const sum = w.reduce((a, x) => a + x, 0)
  const shares = w.map(x => Math.floor(total * x / sum))
  shares[0] += total - shares.reduce((a, x) => a + x, 0)
  return shares
}

// Walks one car through one ordering of jobs and reports what happens.
//
// This is the only place that knows what a day is made of, and every feasibility
// question is answered here rather than guessed at the call site. Returns
// `{ ok: false, reason }` rather than throwing: an infeasible ordering is an
// ordinary answer during search, not an error.
// startMinutes defaults to the day's earliest departure. It is a parameter
// because trimDeparture re-runs a settled round from a later hour to find out
// what the driver actually needs to leave.
// With no start given, the round is tried at the day's normal departure and at
// the earliest one, and the cheaper that works is kept -- so leaving early is
// chosen exactly when the work it makes possible is worth the overtime. A
// given start (a typed departure, trimDeparture's search) is simulated as is.
function simulate (car, jobs, cfg, startMinutes) {
  if (startMinutes != null) return simulateAt(car, jobs, cfg, startMinutes)
  const normal = simulateAt(car, jobs, cfg, cfg.departMinutes)
  if (!(cfg.earliestDepartMinutes < cfg.departMinutes)) return normal
  const early = simulateAt(car, jobs, cfg, cfg.earliestDepartMinutes)
  if (!early.ok) return normal
  if (!normal.ok) return early
  return early.cost < normal.cost - 1e-9 ? early : normal
}

// `leg` (two-day routes only, simulateTwoDay): `from` starts the day at that
// point instead of the depot, with a full charge -- the night stop; `noReturn`
// ends it at the last stop, whose drop must finish by `endBy` (DAY1_LATE).
function simulateAt (car, jobs, cfg, startMinutes, leg) {
  if (!geo.isPoint(geo.DEPOT)) return { ok: false, reason: 'NO_DEPOT' }
  const start = startMinutes
  const from = (leg && leg.from) || geo.DEPOT
  const noReturn = !!(leg && leg.noReturn)

  // Leg distances first: the charging decision needs to know what is still to
  // come, and a driver deciding whether to plug in thinks the same way.
  const points = [from].concat(jobs.map(j => j.point)).concat(noReturn ? [] : [geo.DEPOT])
  const legKm = []
  for (let i = 0; i < points.length - 1; i++) {
    const km = geo.roadDistanceKm(points[i], points[i + 1])
    if (km == null) return { ok: false, reason: 'UNPLACEABLE' }
    legKm.push(km)
  }
  // remainingKm[i] = distance still to drive after arriving at stop i.
  const remainingKm = new Array(legKm.length + 1).fill(0)
  for (let i = legKm.length - 1; i >= 0; i--) remainingKm[i] = remainingKm[i + 1] + legKm[i]

  const isEv = car.fuelType === 'ev'

  let clock = start
  let here = from
  let totalKm = 0
  // Tracked for every vehicle, not only the EVs. A fuel car runs out too; the
  // difference is that its refill is quick, full, and available anywhere.
  let rangeLeft = chargers.startingRangeKm(car) * (1 - cfg.reserveFraction)
  if (rangeLeft <= 0) return { ok: false, reason: 'RANGE' }
  // An out-of-area route is timed on the highway profile (travelTime
  // HIGHWAY_SPEEDS); a Bangkok round never sets this.
  const legOpts = cfg.highway ? { highway: true } : undefined
  let lunchTaken = false
  let lunchAt = null
  let chargeCount = 0
  let refuelCount = 0
  const stops = []
  const charges = []

  for (let i = 0; i < jobs.length; i++) {
    const job = jobs[i]

    const legMinutes = travelTime.travelMinutes(here, job.point, clock, legOpts)
    if (legMinutes == null) return { ok: false, reason: 'UNPLACEABLE' }

    clock += legMinutes
    totalKm += legKm[i]
    rangeLeft -= legKm[i]
    const arrive = clock

    // Waiting is real and is counted. Arriving at 07:29 for an 08:00 window
    // costs 31 minutes, which is exactly why departAt is tuned to arrive AT the
    // window rather than as early as possible.
    const start = Math.max(arrive, job.windowFrom)
    const wait = start - arrive

    // A long wait inside the lunch window IS the break. A driver who reaches a
    // 13:00 appointment at 12:21 eats; pretending they wait empty-handed and
    // then stop again for lunch invents half an hour the day never spends. Four
    // of the five rounds on 2026-09-24 had exactly this wait.
    if (!lunchTaken && wait >= cfg.lunchMinutes) {
      const from = Math.max(arrive, cfg.lunchAfterMinutes)
      const to = Math.min(start, cfg.lunchBeforeMinutes)
      if (to - from >= cfg.lunchMinutes) {
        lunchTaken = true
        lunchAt = from
      }
    }

    if (start + job.installMinutes > job.windowTo) {
      return { ok: false, reason: 'WINDOW', job: job }
    }

    clock = start + job.installMinutes

    // Take on energy here if what remains cannot be driven on what is left.
    // Deciding at the stop rather than mid-leg is both simpler and truer:
    // nobody plugs in on a flyover.
    //
    // WHEN is worth as much as whether. A charge costs the day 38-53 minutes
    // anywhere except over lunch, where the driver eats at the station and it
    // costs 8-23. So a day that needs a charge should take it at the lunch stop
    // -- which is not where it naturally falls: the deficit between range left
    // and distance remaining is constant along a route, so "not enough range"
    // is true from the first stop onwards, and charging at the first moment it
    // is true put every charge at 08:20.
    //
    // Deferring is only safe while the battery can still reach the lunch stop,
    // which is what the second condition tests. This is a heuristic and does not
    // pretend otherwise: it defers one leg at a time rather than searching for
    // the cheapest stop, because it runs inside the insertion loop.
    let charge = null
    const needsCharge = rangeLeft < remainingKm[i + 1]
    const atLunch = !lunchTaken && clock >= cfg.lunchAfterMinutes
    const canReachNextStop = rangeLeft - (legKm[i + 1] || 0) >= 0
    const chargeHere = needsCharge &&
      (atLunch || lunchTaken || !canReachNextStop || i === jobs.length - 1)
    if (chargeHere) {
      if (isEv) {
        if (chargeCount >= cfg.maxChargesPerDay) return { ok: false, reason: 'RANGE', job: job }
        const next = i + 1 < jobs.length ? jobs[i + 1].point : geo.DEPOT
        // Lunch can only absorb a charge if it is lunchtime. Offering it at any
        // hour let an 08:22 charge count as the driver's midday break, which is
        // the same error as counting an installation as one -- just earlier in
        // the day.
        const overLunch = atLunch
        const planned = chargers.planCharge(job.point, next, { absorbedBy: overLunch ? 'lunch' : null })
        // The queue is added even over lunch: a meal absorbs the charging, not
        // a line of cars waiting for the bay.
        charge = Object.assign({}, planned, {
          queueMinutes: cfg.chargeQueueMinutes,
          addedMinutes: planned.addedMinutes + cfg.chargeQueueMinutes
        })
        chargeCount++
        charges.push(Object.assign({ atStopIndex: i, kind: 'charge' }, charge))
        clock += charge.addedMinutes
        totalKm += charge.detourKm
        // Capped, because charging earlier in the day now happens on purpose and
        // a battery does not hold 70 km more than it holds.
        rangeLeft = Math.min(chargers.startingRangeKm(car) * (1 - cfg.reserveFraction),
          rangeLeft + charge.netKmGained)
        // NOTE what is deliberately NOT done here: lunch is not marked taken.
        //
        // Absorbing a charge into lunch does not make the half hour disappear --
        // it means the driver spends the SAME half hour at a station instead of
        // anywhere else. The lunch block below therefore still runs and still
        // adds its 30 minutes; what absorption saves is the charge's own half
        // hour on top of it, leaving only the access overhead. Marking lunch
        // taken here instead made a charge cost 8 minutes and the driver's meal
        // cost nothing at all, which is half an hour a day of fiction.
      } else {
        // A tank goes back to full, so one stop is usually the whole answer.
        if (refuelCount >= cfg.maxRefuelsPerDay) return { ok: false, reason: 'RANGE', job: job }
        refuelCount++
        charge = {
          kind: 'refuel',
          name: 'เติม' + (car.fuelType === 'ngv' ? 'แก๊ส' : 'น้ำมัน'),
          source: 'generic',
          detourKm: 0,
          addedMinutes: cfg.refuelMinutes,
          absorbed: false,
          kmGained: chargers.startingRangeKm(car) * (1 - cfg.reserveFraction),
          netKmGained: chargers.startingRangeKm(car) * (1 - cfg.reserveFraction)
        }
        charges.push(Object.assign({ atStopIndex: i }, charge))
        clock += charge.addedMinutes
        rangeLeft = charge.netKmGained
      }
    }

    // Otherwise at the first stop that ends after the window opens. Flexible by
    // the delivery team's own description, so the planner places it rather than
    // fixing a time -- but it does have to happen, and a round where the first
    // chance falls after 13:00 says so rather than quietly eating at 15:00.
    if (!lunchTaken && clock >= cfg.lunchAfterMinutes) {
      lunchAt = clock
      clock += cfg.lunchMinutes
      lunchTaken = true
    }

    stops.push({
      job: job,
      legKm: legKm[i],
      legMinutes: legMinutes,
      arriveAt: arrive,
      waitMinutes: wait,
      startAt: start,
      installMinutes: job.installMinutes,
      departAt: clock,
      charge: charge
    })

    here = job.point
  }

  // Lunch still owed at the end of a short day.
  if (!lunchTaken && jobs.length) {
    lunchAt = clock
    clock += cfg.lunchMinutes
  }

  let returnMinutes = 0
  if (!noReturn) {
    returnMinutes = travelTime.travelMinutes(here, geo.DEPOT, clock, legOpts)
    if (returnMinutes == null) return { ok: false, reason: 'UNPLACEABLE' }
    clock += returnMinutes
    totalKm += legKm[legKm.length - 1]
    rangeLeft -= legKm[legKm.length - 1]
    if (clock > cfg.mustReturnMinutes) return { ok: false, reason: 'TOO_LATE' }
  } else if (leg.endBy != null && stops.length && stops[stops.length - 1].departAt > leg.endBy) {
    return { ok: false, reason: 'DAY1_LATE' }
  }
  // One rule for both fuel types now: whatever is in the tank or the battery
  // had to cover the last leg home.
  if (rangeLeft < 0) return { ok: false, reason: 'RANGE' }

  const fuel = totalKm * (car.costPerKm || 0)
  // Both ends of the day: the crew arrives crewPrepMinutes before the car
  // leaves, so a 06:30 departure is two hours early, and every minute back
  // after workEnd is paid too.
  //
  // Priced on when the car WILL leave, not when this simulation started it:
  // time spent waiting at the first stop beyond the buffer is trimmed off the
  // departure afterwards (trimDeparture), so charging overtime for it would
  // make an early start look dearer than it is and the planner would never
  // choose one that only needed to be a little early.
  const slack = stops.length ? Math.max(0, stops[0].waitMinutes - cfg.arriveBufferMinutes) : 0
  const leaves = start + slack
  const overtimeMinutes = Math.max(0, cfg.workStartMinutes - (leaves - cfg.crewPrepMinutes)) +
    Math.max(0, clock - cfg.workEndMinutes)
  const overtime = overtimeMinutes * cfg.overtimeCostPerMinute
  const duration = clock - leaves
  const spare = car.spare ? cfg.sparePenalty : 0
  const chargeRisk = chargeCount * cfg.chargePenalty

  return {
    ok: true,
    car: car,
    stops: stops,
    charges: charges,
    km: totalKm,
    fuelCost: fuel,
    overtimeCost: overtime,
    overtimeMinutes: overtimeMinutes,
    spareCost: spare,
    chargeRiskCost: chargeRisk,
    cost: fuel + overtime + spare + chargeRisk + duration * cfg.minuteCost,
    startAt: start,
    endAt: clock,
    returnMinutes: returnMinutes,
    durationMinutes: duration,
    // Every working day contains exactly one of these. It is reported because
    // it was once silently false: a charge absorbed by a long installation was
    // credited as lunch, and the driver's break vanished from the timetable.
    lunchTaken: lunchTaken || jobs.length > 0,
    lunchAt: lunchAt,
    // True when the driver could not be fed inside 11:00-14:00. Reported rather
    // than hidden: it is a real thing to fix in the day, not a rounding error.
    lunchLate: lunchAt != null && lunchAt > cfg.lunchBeforeMinutes,
    rangeLeftKm: isEv ? rangeLeft : null
  }
}

// A route over two days (out-of-area only): day 1 from the depot through
// jobs[0..split), ending at the last of them; the night there, full again;
// day 2 from that stop through jobs[split..) and home. Day 1 is tried at
// twoDayDepartAt and twoDayEarliestDepartAt, the cheaper that works kept, as
// simulate does for one day. Day 2's first drop is at twoDayDay2StartAt or its
// window, whichever is later; the driver leaves the night stop that leg early.
// `memo` (bestTwoDay) remembers each day by its stops: the permutations share
// prefixes and suffixes. -> one route, or { ok: false, reason }.
function simulateTwoDay (car, jobs, cfg, split, memo) {
  if (!(split >= 1 && split < jobs.length)) return { ok: false, reason: 'SPLIT' }
  const m = memo || {}
  const first = jobs.slice(0, split)
  const second = jobs.slice(split)
  const ids = list => list.map(j => j.id).join(',')

  const k1 = '1|' + ids(first)
  if (!m[k1]) {
    let day1 = null
    let failed = null
    const starts = [cfg.twoDayDepartMinutes, cfg.twoDayEarliestDepartMinutes]
      .filter((t, i, a) => t != null && a.indexOf(t) === i)
    starts.forEach(t => {
      const s = simulateAt(car, first, cfg, t, { noReturn: true, endBy: cfg.twoDayDay1EndMinutes })
      if (!s.ok) { failed = failed || s; return }
      if (!day1 || s.cost < day1.cost - 1e-9) day1 = s
    })
    m[k1] = day1 || failed || { ok: false, reason: 'SPLIT' }
  }
  const day1 = m[k1]
  if (!day1.ok) return day1

  const night = first[first.length - 1]
  const k2 = '2|' + night.id + '|' + ids(second)
  if (!m[k2]) {
    const legOpts = cfg.highway ? { highway: true } : undefined
    const target = Math.max(cfg.twoDayDay2StartMinutes, second[0].windowFrom)
    const lead = travelTime.travelMinutes(night.point, second[0].point, target, legOpts)
    m[k2] = lead == null
      ? { ok: false, reason: 'UNPLACEABLE' }
      : simulateAt(car, second, cfg, target - lead, { from: night.point })
  }
  const day2 = m[k2]
  if (!day2.ok) return day2
  return joinDays(day1, day2, split)
}

// Two simulated days as one route: stops in order, each with its day; costs
// and km summed (the spare-car penalty once); startAt is day 1's departure and
// endAt day 2's return. dayParts says where each day starts and ends.
function joinDays (day1, day2, split) {
  const last1 = day1.stops[day1.stops.length - 1]
  return {
    ok: true,
    car: day1.car,
    days: 2,
    split: split,
    dayParts: [
      { day: 1, startAt: day1.startAt, endAt: last1.departAt, km: day1.km },
      { day: 2, startAt: day2.startAt, endAt: day2.endAt, km: day2.km }
    ],
    stops: day1.stops.map(s => Object.assign({}, s, { day: 1 }))
      .concat(day2.stops.map(s => Object.assign({}, s, { day: 2 }))),
    charges: day1.charges.concat(day2.charges.map(c => Object.assign({}, c, { atStopIndex: c.atStopIndex + split }))),
    km: day1.km + day2.km,
    fuelCost: day1.fuelCost + day2.fuelCost,
    overtimeCost: day1.overtimeCost + day2.overtimeCost,
    overtimeMinutes: day1.overtimeMinutes + day2.overtimeMinutes,
    spareCost: day1.spareCost,
    chargeRiskCost: day1.chargeRiskCost + day2.chargeRiskCost,
    cost: day1.cost + day2.cost - day2.spareCost,
    startAt: day1.startAt,
    endAt: day2.endAt,
    returnMinutes: day2.returnMinutes,
    durationMinutes: day1.durationMinutes + day2.durationMinutes,
    lunchTaken: true,
    lunchAt: day1.lunchAt,
    lunchLate: !!(day1.lunchLate || day2.lunchLate),
    rangeLeftKm: day2.rangeLeftKm
  }
}

// The cost of adding one job to one route, or null if it will not fit.
function insertionCost (car, current, job, position, cfg) {
  const order = current.slice()
  order.splice(position, 0, job)
  const after = simulate(car, order, cfg)
  if (!after.ok) return null
  return { cost: after.cost, result: after, order: order }
}

// Hardest first. A job that is far out, tightly windowed and long is the one
// most likely to fit nowhere once the easy work has filled the routes, so it
// gets first choice of position while there is still room to give.
function difficulty (job) {
  const far = geo.distanceFromDepotKm(job.point) || 0
  const windowWidth = job.windowTo - job.windowFrom
  return far * 2 + job.installMinutes - windowWidth
}

// Try to move each job to a cheaper place, and to reverse route segments that
// cross over themselves. Cheap, bounded, and worth 10-15% of the kilometres a
// pure insertion build leaves on the table.
function improve (routes, cfg) {
  let improved = true
  let passes = 0

  while (improved && passes < cfg.improvePasses) {
    improved = false
    passes++

    // 2-opt: reversing a segment removes a crossing.
    routes.forEach(route => {
      const jobs = route.stops.map(s => s.job)
      for (let i = 0; i < jobs.length - 1; i++) {
        for (let k = i + 1; k < jobs.length; k++) {
          const candidate = jobs.slice(0, i)
            .concat(jobs.slice(i, k + 1).reverse())
            .concat(jobs.slice(k + 1))
          const sim = simulate(route.car, candidate, cfg)
          if (sim.ok && sim.cost < route.cost - 1e-9) {
            Object.assign(route, sim)
            improved = true
            return
          }
        }
      }
    })

    // Swap two cars' whole rounds.
    //
    // Relocating one job at a time cannot fix "the wrong CAR is driving this
    // route". On a real 24-job day 2ฒฆ-2748 (฿2.10) held the longest round at
    // 231 km through every amount of relocation, because no single move made
    // things better even though exchanging the entire round with a cheaper car
    // did. Geometry is untouched by a swap -- same stops, same order, same
    // kilometres -- so the whole gain is the difference in ฿/km, which is
    // exactly the eightfold spread this planner exists to exploit.
    //
    // Cheap: two simulations per pair, fifteen pairs for six cars.
    for (let a = 0; a < routes.length && !improved; a++) {
      for (let b = a + 1; b < routes.length; b++) {
        const A = routes[a]
        const B = routes[b]
        if (!A.stops.length && !B.stops.length) continue
        // A swap moves every stop of both rounds -- a held pin with them.
        if (A.stops.concat(B.stops).some(x => pinHeld(x.job))) continue

        const aJobs = A.stops.map(s => s.job)
        const bJobs = B.stops.map(s => s.job)
        const aOnB = simulate(B.car, aJobs, cfg)
        const bOnA = simulate(A.car, bJobs, cfg)
        if (!aOnB.ok || !bOnA.ok) continue

        if (aOnB.cost + bOnA.cost < A.cost + B.cost - 1e-9) {
          Object.assign(A, bOnA)
          Object.assign(B, aOnB)
          improved = true
          break
        }
      }
    }

    // Relocate: a job may be cheaper on another car entirely. This is what
    // finishes the cheap-car-long-route inversion that insertion order only
    // approximates.
    for (let a = 0; a < routes.length && !improved; a++) {
      const from = routes[a]
      for (let s = 0; s < from.stops.length; s++) {
        const job = from.stops[s].job
        if (pinHeld(job)) continue
        const without = from.stops.map(x => x.job).filter((_, idx) => idx !== s)
        const fromSim = simulate(from.car, without, cfg)
        if (!fromSim.ok) continue

        for (let b = 0; b < routes.length; b++) {
          if (a === b) continue
          const to = routes[b]
          const toJobs = to.stops.map(x => x.job)
          for (let p = 0; p <= toJobs.length; p++) {
            const ins = insertionCost(to.car, toJobs, job, p, cfg)
            if (!ins) continue
            const before = from.cost + to.cost
            const after = fromSim.cost + ins.cost
            if (after < before - 1e-9) {
              Object.assign(from, fromSim)
              Object.assign(to, ins.result)
              improved = true
              break
            }
          }
          if (improved) break
        }
        if (improved) break
      }
    }
  }

  return passes
}

// ---------------------------------------------------------------------------
// Looking further than one move at a time
// ---------------------------------------------------------------------------
//
// improve() stops at the first plan no SINGLE move can better -- and on a busy
// day that plan is often well short of the best one, because a better day
// needs several stops to change cars together. ย้ายไปรถ showed it: on a
// synthetic 24-stop day, 32 of 56 hand moves made the whole day cheaper by the
// planner's own measure, by up to ฿530.
//
// So, repeatedly: take a few stops out of the plan -- usually ones near one
// another, since that is what a better plan trades between two cars, and now
// and then a scattered few -- put each back where it is now cheapest, and
// keep the result if it is not much worse than the day being searched from.
//
// "Not much worse" is the point. Keeping only strict improvements stalled on
// exactly the plans ย้ายไปรถ exposed: no small change helped, while a bigger
// one did. Allowing a small step uphill -- ฿searchSlack at first, shrinking
// to nothing by the last round -- lets the search walk out of that dip. The
// best day seen is kept throughout and is what the search returns, so the
// plan can never come out worse than the one improve() handed it.
//
// Deterministic -- a fixed seed, and a fixed number of rounds rather than a
// time limit -- so the same day always gives the same plan. ย้ายไปรถ's
// preview depends on that: it is the plan the move will produce only if
// planning the same input twice gives the same answer.
//
// A stop moved by hand is never taken out. Work that could not be placed at
// all is offered a place again at the end, since the day may now have room.
function searchWider (routes, unassigned, cfg) {
  const rounds = cfg.searchRounds
  if (!(rounds > 0)) return { rounds: 0, kept: 0 }
  const rand = seededRandom(cfg.searchSeed)
  const totalOf = rs => rs.reduce((a, r) => a + (r.cost || 0), 0)
  // A route's result is replaced, never edited, so keeping the objects is a
  // complete snapshot of a day.
  const snapshot = () => routes.map(r => Object.assign({}, r))
  let best = snapshot()
  let bestTotal = totalOf(routes)
  let kept = 0

  for (let round = 0; round < rounds; round++) {
    const movable = []
    routes.forEach((r, ri) => r.stops.forEach(s => { if (!pinHeld(s.job)) movable.push({ job: s.job, ri: ri }) }))
    if (movable.length < 2) break

    // A seed stop and its nearest neighbours, 2 to 6 of them in all -- or, one
    // round in four, that many picked anywhere.
    const seed = movable[Math.floor(rand() * movable.length)]
    const size = Math.min(movable.length, 2 + Math.floor(rand() * 5))
    const scattered = rand() < 0.25
    const taken = movable
      .map(m => ({ m: m, d: scattered ? rand() : (geo.roadDistanceKm(seed.job.point, m.job.point) || 0) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, size)
      .map(x => x.m)
    const takenIds = new Set(taken.map(t => t.job.id))

    // The day without them. Taking stops out of a round can in principle break
    // it (a stop that was absorbing a wait), so a round that stops working
    // abandons this try.
    const trial = routes.map(r => ({ car: r.car, jobs: r.stops.map(s => s.job), sim: r }))
    let broken = false
    trial.forEach(t => {
      if (!t.jobs.some(j => takenIds.has(j.id))) return
      t.jobs = t.jobs.filter(j => !takenIds.has(j.id))
      t.sim = simulate(t.car, t.jobs, cfg)
      if (!t.sim.ok) broken = true
    })
    if (broken) continue

    const order = taken.map(t => t.job).sort((a, b) => difficulty(b) - difficulty(a))

    // Now and then, all of them onto a car that is not going out today.
    //
    // Put back one at a time, the first stop on an idle car carries that car's
    // whole drive out and back, so it always looks dearer than squeezing it
    // onto a car already on the road -- and the search never sends another
    // car, however late the others run. On 2026-09-29 that held a three-car
    // day at ฿632 of overtime when a fourth car cut it to ~฿60 and the whole
    // day by ฿300; ย้ายไปรถ found it, the search could not. Moving a group
    // together is what lets the idle car's drive be shared.
    let placedAll = false
    const idle = trial.filter(t => !t.jobs.length)
    if (idle.length && rand() < cfg.searchOpenCar) {
      const t = idle[Math.floor(rand() * idle.length)]
      let seq = []
      let sim = null
      for (const job of order) {
        let best = null
        for (let p = 0; p <= seq.length; p++) {
          const jobs = seq.slice()
          jobs.splice(p, 0, job)
          const s = simulate(t.car, jobs, cfg)
          if (s.ok && (!best || s.cost < best.sim.cost)) best = { jobs: jobs, sim: s }
        }
        if (!best) { seq = null; break }
        seq = best.jobs
        sim = best.sim
      }
      if (seq) {
        t.jobs = seq
        t.sim = sim
        placedAll = true
      }
    }

    // Otherwise back in, hardest first, each at its cheapest position anywhere.
    if (!placedAll) {
      placedAll = true
      for (const job of order) {
        let cheapest = null
        trial.forEach(t => {
          for (let p = 0; p <= t.jobs.length; p++) {
            const jobs = t.jobs.slice()
            jobs.splice(p, 0, job)
            const sim = simulate(t.car, jobs, cfg)
            if (!sim.ok) continue
            const delta = sim.cost - t.sim.cost
            if (!cheapest || delta < cheapest.delta) cheapest = { delta: delta, t: t, jobs: jobs, sim: sim }
          }
        })
        if (!cheapest) { placedAll = false; break }
        cheapest.t.jobs = cheapest.jobs
        cheapest.t.sim = cheapest.sim
      }
    }
    if (!placedAll) continue

    const slack = cfg.searchSlack * (1 - round / rounds)
    const trialTotal = totalOf(trial.map(t => t.sim))
    if (trialTotal < totalOf(routes) + slack - 1e-6) {
      trial.forEach((t, i) => { if (t.sim !== routes[i]) Object.assign(routes[i], t.sim) })
      if (trialTotal < bestTotal - 1e-6) {
        best = snapshot()
        bestTotal = trialTotal
        kept++
      }
    }
  }

  // The best day seen, which may be several rounds back.
  best.forEach((b, i) => {
    Object.keys(routes[i]).forEach(k => { delete routes[i][k] })
    Object.assign(routes[i], b)
  })

  // Anything left unplaced gets one more try at the finished day.
  for (let u = unassigned.length - 1; u >= 0; u--) {
    const job = unassigned[u].job
    if (unassigned[u].reason === 'UNPLACEABLE') continue
    let best = null
    routes.forEach(r => {
      const current = r.stops.map(s => s.job)
      for (let p = 0; p <= current.length; p++) {
        const jobs = current.slice()
        jobs.splice(p, 0, job)
        const sim = simulate(r.car, jobs, cfg)
        if (!sim.ok) continue
        const delta = sim.cost - (r.cost || 0)
        if (!best || delta < best.delta) best = { delta: delta, r: r, sim: sim }
      }
    })
    if (best) {
      Object.assign(best.r, best.sim)
      unassigned.splice(u, 1)
    }
  }

  return { rounds: rounds, kept: kept }
}

// mulberry32: small, fast, and the same sequence on every machine.
function seededRandom (seed) {
  let a = (seed >>> 0) || 1
  return function () {
    a = (a + 0x6D2B79F5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// Why a job could not be placed, in the words staff need rather than the
// planner's. The distinction that matters: WINDOW and RANGE mean "not on this
// day, on this fleet"; UNPLACEABLE means the booking itself is incomplete.
// ---------------------------------------------------------------------------
// When each car actually leaves
// ---------------------------------------------------------------------------
//
// `departAt` is the earliest a car may leave, and leaving that early is only
// worth it for the round that needs it. On 2026-09-24, 1ฒบ-7127 left at 06:30
// and reached its 08:00 appointment at 07:26: 34 minutes of a paid day spent
// standing outside a shuttered shop. Three of the five cars were doing some
// version of this, 49 minutes between them.
//
// Holding a round back is safe to do AFTER routing, and this is the reason:
// travelTime integrates band by band, so arrival is monotonic in departure --
// leaving later can never arrive earlier. The set of departures that still
// reach the first stop on time is therefore a prefix, which a binary search can
// find exactly, and every stop after the first is untouched because the first
// one still starts at the same minute.
//
// What it does NOT fix is overtime, and it should not be sold as if it did.
// Overtime is set by when the day ENDS. On that same day the only car with any
// was 4ฒฎ-7073, which had no wait to trim at all.
function trimDeparture (route, cfg) {
  const jobs = route.stops.map(s => s.job)
  if (!jobs.length) return null

  const first = route.stops[0]
  const maxShift = Math.floor(first.waitMinutes - cfg.arriveBufferMinutes)
  if (!(maxShift > 0)) return null

  // Two things must survive the shift: the first stop still begins at the same
  // minute, and the buffer is still there. The second is not implied by the
  // first -- a later departure can push part of the drive into a slower band,
  // which eats the cushion without moving the appointment.
  const keeps = sim => sim.ok && sim.stops.length === route.stops.length &&
    sim.stops[0].startAt === first.startAt &&
    sim.stops[0].waitMinutes >= cfg.arriveBufferMinutes - 1e-9

  let lo = 1
  let hi = maxShift
  let best = null
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2)
    const sim = simulate(route.car, jobs, cfg, route.startAt + mid)
    if (keeps(sim)) { best = sim; lo = mid + 1 } else { hi = mid - 1 }
  }
  return best
}

// Staff first, arithmetic second. A typed departure wins outright -- they know
// about the driver who must load in the morning and the planner does not -- but
// an override that breaks the round is refused rather than obeyed, because a
// plan that cannot be driven is not an instruction, it is a mistake nobody
// caught until 16:00.
function settleDeparture (route, cfg) {
  if (!route.stops.length) return
  // Measured from where routing left the car, not from the day's departure:
  // a round the planner sent early is not "trimmed" by being later than 06:30.
  const earliest = route.startAt
  const manual = cfg.departByCarMinutes[route.car.regNum]

  if (manual != null) {
    const sim = simulate(route.car, route.stops.map(s => s.job), cfg, manual)
    if (sim.ok) {
      Object.assign(route, sim)
      route.departSource = 'manual'
      route.idleTrimmed = 0
      return
    }
    route.departNote = sim.reason
  }

  const trimmed = cfg.trimDepartures ? trimDeparture(route, cfg) : null
  if (trimmed) Object.assign(route, trimmed)
  route.departSource = route.departNote ? 'manual-rejected' : 'auto'
  route.idleTrimmed = route.startAt - earliest
}

const UNASSIGNED_TH = {
  UNPLACEABLE: 'ระบุตำแหน่งไม่ได้ — ต้องมีตำบล/อำเภอ หรือพิกัด',
  WINDOW: 'ไม่มีรถคันใดไปถึงได้ทันช่วงเวลาที่ลูกค้าเลือก',
  RANGE: 'ระยะทางเกินที่รถวิ่งได้ แม้ชาร์จระหว่างทางแล้ว',
  TOO_LATE: 'ใส่แล้วรถกลับถึงคลังช้าเกินกำหนด',
  NO_CAR: 'ไม่มีรถว่างพอสำหรับงานนี้'
}

// Two plans, the first without charging. This is the delivery team's own rule
// -- charge as little as possible -- made exact rather than approximated by a
// weight: a day that can be covered on what the batteries hold is never given
// a charge, however many baht one would save. Only when that plan strands work
// is the day planned again with charging allowed, and the second plan is kept
// only if it actually routes more. `summary.chargingNeeded` says which it was.
function planDay (input) {
  const cfg = config(input && input.config)
  const noCharge = planWith(input, Object.assign({}, cfg, { maxChargesPerDay: 0 }))
  noCharge.summary.chargingNeeded = false

  const stranded = noCharge.unassigned.some(u => u.reason !== 'UNPLACEABLE')
  const hasEv = ((input && input.cars) || []).some(c => c && c.fuelType === 'ev' && c.size !== 'large')
  if (!stranded || !hasEv || !(cfg.maxChargesPerDay > 0)) return noCharge

  const charging = planWith(input, cfg)
  if (charging.summary.unassigned >= noCharge.summary.unassigned) return noCharge
  charging.summary.chargingNeeded = charging.summary.charges > 0
  return charging
}

// The two plans the page offers side by side, because which is right changes
// from day to day and the delivery team knows things this module does not --
// whether a spare crew has other work, how busy the chargers near a round are.
//
//   minCharge  planDay(): charging only when nothing else fits. The default,
//              because it is the safe choice; saving fuel is a decision.
//   minFuel    charging allowed at no risk price -- the cheapest fuel. Any
//              charge still carries the queue allowance, so choosing it means
//              accepting a charge, not a late customer.
//
// minFuel is null when it would not be a real choice: when it routes the same
// stops on the same cars, when it strands more work, or when it saves nothing.
// A comparison where nothing differs is noise.
//
// Each stop carries `otherCar`: the plate the OTHER plan gives it when that
// differs (null otherwise, 'unassigned' if the other plan could not place it),
// so a planner switching between the two sees what moved without matching
// customers across two lists by eye.
//
// Each plan is the best of cfg.planRuns runs, each searching from its own seed
// (planBest): where the search ends up depends on where it starts, and on a
// busy day the spread is wide -- 2026-09-29 ran from ฿731 to ฿958 across seeds.
// `onProgress({ done, total })` is called after each run. Each plan also
// carries setAside and trips (planTrips).
function planOptions (input, onProgress) {
  const cfg = config(input && input.config)
  const runs = Math.max(1, Math.floor(cfg.planRuns) || 1)
  const both = cfg.maxChargesPerDay > 0
  const total = runs * (both ? 2 : 1)
  let done = 0
  const tick = () => { done++; if (onProgress) onProgress({ done: done, total: total }) }

  // The out-of-area routes, planned against each plan's own leftover cars --
  // so the two plans can suggest different trucks, and switching between them
  // switches the suggestion too.
  const setAside = (input && input.setAside) || []
  // A route already sent to คิวรถเล็ก holds its car: the Bangkok plan is made
  // without it, so neither a round nor ย้ายไปรถ can take it (sentPlates).
  const held = sentPlates(setAside)
  // Plus the cars still out on day 2 of a route sent yesterday (carriedPlates).
  const carried = (input && input.carried) || []
  const heldPlates = Object.keys(held).map(k => held[k]).concat(carried)
  if (heldPlates.length) {
    input = Object.assign({}, input, {
      cars: ((input && input.cars) || []).filter(c => c && heldPlates.indexOf(c.regNum) === -1),
      fleet: (input && input.cars) || []
    })
  }
  const withTrips = p => {
    if (!p) return p
    p.setAside = setAside
    // fleet keeps sent cars on purpose (a sent route stays on its own car); a
    // carried car belongs to yesterday's route and to nobody today.
    p.trips = planTrips(setAside, ((input && (input.fleet || input.cars)) || []).filter(c => c && carried.indexOf(c.regNum) === -1), p, p.config || cfg)
    return p
  }

  const minCharge = planBest(input, 'minCharge', runs, tick)
  minCharge.mode = 'minCharge'
  if (!both) return { minCharge: withTrips(minCharge), minFuel: null }

  const minFuel = planBest(input, 'minFuel', runs, tick)
  minFuel.mode = 'minFuel'
  minFuel.summary.chargingNeeded = minCharge.summary.chargingNeeded && minFuel.summary.charges > 0

  const offered = minFuel.summary.unassigned <= minCharge.summary.unassigned &&
    minFuel.summary.fuelCost < minCharge.summary.fuelCost - 0.5 &&
    !sameAssignment(minCharge, minFuel)
  if (!offered) return { minCharge: withTrips(minCharge), minFuel: null }

  markOtherCar(minCharge, minFuel)
  markOtherCar(minFuel, minCharge)
  return { minCharge: withTrips(minCharge), minFuel: withTrips(minFuel) }
}

// The best of `runs` plans of one kind, each from a different search seed.
// Deterministic: the seeds are fixed, so the same day always gives the same
// plan. Fewer stranded stops wins first, then the planner's own objective.
function planBest (input, mode, runs, onRun) {
  const cfg = config(input && input.config)
  const objective = p => p.routes.reduce((a, r) => a + r.cost, 0)
  let best = null
  for (let k = 0; k < runs; k++) {
    const seeded = Object.assign({}, input, {
      config: Object.assign({}, (input && input.config) || {}, { searchSeed: cfg.searchSeed + k * 7919 })
    })
    const plan = planFor(seeded, mode)
    if (onRun) onRun()
    if (!best || plan.summary.unassigned < best.summary.unassigned ||
        (plan.summary.unassigned === best.summary.unassigned && objective(plan) < objective(best) - 1e-6)) {
      best = plan
    }
  }
  return best
}

// One of the two plans by name, one run.
function planFor (input, mode) {
  if (mode !== 'minFuel') return planDay(input)
  const cfg = config(input && input.config)
  return planWith(input, Object.assign({}, cfg, { chargePenalty: 0 }))
}

// Why a car cannot take a stop someone tried to move onto it.
const PIN_REASON_TH = {
  WINDOW: 'ไปไม่ทันช่วงเวลาที่ลูกค้าเลือก',
  RANGE: 'ระยะทางเกินที่รถวิ่งได้ในแผนนี้',
  TOO_LATE: 'กลับถึงบริษัทเกิน 19:00',
  NO_CAR: 'รถคันนี้ไม่อยู่ในแผนวันนี้'
}

const totalCost = plan => plan.summary.fuelCost + plan.summary.overtimeCost

// What ย้ายไปรถ offers for one stop: every other small car, each with the
// day as it would be after moving just this stop there -- or the reason that
// car cannot take it.
//
// Only the two cars involved change: the stop leaves its car, goes into the
// other at its cheapest position, and both rounds are re-ordered. Every other
// car keeps exactly what it had. That is what "move this stop" means to the
// person asking, it is instant, and the plan it shows is the plan they get --
// clicking applies it as is. (The first version re-planned the whole day for
// each car, and its numbers mixed the move with the planner happening to land
// somewhere better: ถูกลง ฿217 that the move itself had little to do with.)
//
// The stop is pinned to its new car, so the next วางแผน keeps it there.
function moveChoices (plan, jobId) {
  const onCar = carByJob(plan)[jobId] || null
  const cars = ((plan.cars || []).filter(c => c && c.size !== 'large'))
    .filter(c => c.regNum !== onCar)
    .sort((x, y) => (x.costPerKm || 0) - (y.costPerKm || 0))
  const usesSpare = p => p.routes.some(r => r.car.spare)
  const choices = cars.map(car => {
    const moved = moveLocal(plan, jobId, car.regNum)
    if (!moved.ok) return { regNum: car.regNum, ok: false, reason: moved.reason, message: PIN_REASON_TH[moved.reason] }
    const after = moved.plan
    const route = after.routes.find(r => r.car.regNum === car.regNum)
    return {
      regNum: car.regNum,
      ok: true,
      total: totalCost(after),
      delta: totalCost(after) - totalCost(plan),
      fuelCost: after.summary.fuelCost,
      overtimeCost: after.summary.overtimeCost,
      carReturn: route ? route.endAt : null,
      latestReturn: after.summary.latestReturn,
      unassigned: after.summary.unassigned,
      spareAdded: usesSpare(after) && !usesSpare(plan),
      carsDelta: after.summary.cars - plan.summary.cars,
      plan: after
    }
  })
  const pins = (plan.config && plan.config.pins) || {}
  return { onCar: onCar, pinned: pins[jobId] || null, total: totalCost(plan), choices: choices }
}

// `plan` with one stop moved onto `regNum`: { ok, plan } or { ok: false, reason }.
// Pure -- the plan passed in is not touched.
function moveLocal (plan, jobId, regNum) {
  const cfg = plan.config
  const from = plan.routes.find(r => r.stops.some(st => st.job.id === jobId))
  if (!from) return { ok: false, reason: 'NO_CAR' }
  const to = plan.routes.find(r => r.car.regNum === regNum)
  const car = to ? to.car : (plan.cars || []).find(c => c.regNum === regNum)
  if (!car || car.size === 'large' || from.car.regNum === regNum) return { ok: false, reason: 'NO_CAR' }

  const stop = from.stops.find(st => st.job.id === jobId)
  const job = Object.assign({}, stop.job, { pin: { regNum: regNum, held: true } })
  const fromJobs = from.stops.map(st => st.job).filter(j => j.id !== jobId)
  const toJobs = to ? to.stops.map(st => st.job) : []

  let best = null
  const reasons = {}
  for (let p = 0; p <= toJobs.length; p++) {
    const jobs = toJobs.slice()
    jobs.splice(p, 0, job)
    const sim = simulate(car, jobs, cfg)
    if (!sim.ok) { reasons[sim.reason] = true; continue }
    if (!best || sim.cost < best.sim.cost) best = { jobs: jobs, sim: sim }
  }
  if (!best) {
    return { ok: false, reason: reasons.WINDOW ? 'WINDOW' : reasons.RANGE ? 'RANGE' : reasons.TOO_LATE ? 'TOO_LATE' : 'NO_CAR' }
  }
  const newTo = reorder(car, best.jobs, cfg)
  const newFrom = fromJobs.length ? reorder(from.car, fromJobs, cfg) : null
  if (newFrom && !newFrom.ok) return { ok: false, reason: newFrom.reason }
  if (newFrom) settleDeparture(newFrom, cfg)
  settleDeparture(newTo, cfg)

  // Every other car as it was -- copied, since report() writes each stop's
  // flags, and the plan passed in must not change under the caller.
  const copy = r => Object.assign({}, r, { stops: r.stops.map(st => Object.assign({}, st)) })
  const routes = []
  plan.routes.forEach(r => {
    if (r === from) { if (newFrom) routes.push(newFrom) } else if (r === to) routes.push(newTo)
    else routes.push(copy(r))
  })
  if (!to) routes.push(newTo)
  const reported = report(routes, plan.unassigned, cfg, plan.summary.improvePasses)
  reported.summary.chargingNeeded = plan.summary.chargingNeeded
  const next = Object.assign({}, plan, reported)
  // A stop moved onto a car can take it from a route, or free one: the routes
  // are re-planned against the day as it now stands.
  if (plan.setAside) next.trips = planTrips(plan.setAside, plan.cars, next, cfg)
  return { ok: true, plan: next }
}

// One round's best order by reversing segments until none helps (2-opt).
function reorder (car, jobs, cfg) {
  let current = simulate(car, jobs, cfg)
  if (!current.ok) return current
  let order = jobs.slice()
  let improved = true
  for (let pass = 0; improved && pass < 20; pass++) {
    improved = false
    for (let i = 0; i < order.length - 1 && !improved; i++) {
      for (let k = i + 1; k < order.length; k++) {
        const candidate = order.slice(0, i).concat(order.slice(i, k + 1).reverse()).concat(order.slice(k + 1))
        const sim = simulate(car, candidate, cfg)
        if (sim.ok && sim.cost < current.cost - 1e-9) { current = sim; order = candidate; improved = true; break }
      }
    }
  }
  return current
}

// Each stop's otherCar, against the other plan -- again after a stop moves.
function pairPlans (a, b) {
  if (!a || !b) return
  markOtherCar(a, b)
  markOtherCar(b, a)
}

function pinFor (cfg, job) {
  const pins = cfg && cfg.pins
  return pins && job && job.id != null && pins[job.id] ? pins[job.id] : null
}

// Only a pin that was honoured holds a stop in place. One the car refused is
// placed like any other stop, and improve() may move it as it would any other.
function pinHeld (job) {
  return !!(job && job.pin && job.pin.held)
}

function carByJob (plan) {
  const out = {}
  plan.routes.forEach(r => r.stops.forEach(s => { out[s.job.id] = r.car.regNum }))
  return out
}

function sameAssignment (a, b) {
  const ca = carByJob(a)
  const cb = carByJob(b)
  const ids = Object.keys(ca)
  return ids.length === Object.keys(cb).length && ids.every(id => ca[id] === cb[id])
}

function markOtherCar (plan, other) {
  const there = carByJob(other)
  plan.routes.forEach(r => r.stops.forEach(s => {
    const car = there[s.job.id]
    s.otherCar = car == null ? 'unassigned' : car !== r.car.regNum ? car : null
  }))
}

function planWith (input, cfg) {
  const bookings = (input && input.bookings) || []
  const fleet = (input && input.cars) || []

  // Small cars only. Bangkok and the metro provinces are served by the small
  // fleet; the large trucks are upcountry vehicles and are not candidates here.
  const cars = fleet
    .filter(c => c && c.size !== 'large')
    .slice()
    .sort((a, b) => (a.costPerKm || 0) - (b.costPerKm || 0))

  // One job per destination, not per booking -- see groupVisits.
  const jobs = groupVisits(bookings).map(toVisitJob)
  const unassigned = []

  const placeable = jobs.filter(j => {
    if (!j.point || !j.point.precision) {
      unassigned.push({ job: j, reason: 'UNPLACEABLE', message: UNASSIGNED_TH.UNPLACEABLE })
      return false
    }
    return true
  })

  if (!cars.length) {
    placeable.forEach(j => unassigned.push({ job: j, reason: 'NO_CAR', message: UNASSIGNED_TH.NO_CAR }))
    return Object.assign(report([], unassigned, cfg, 0), { cars: fleet })
  }

  const routes = cars.map(car => ({
    car: car,
    stops: [],
    charges: [],
    km: 0,
    cost: 0,
    fuelCost: 0,
    overtimeCost: 0,
    spareCost: 0,
    chargeRiskCost: 0,
    startAt: cfg.departMinutes,
    endAt: cfg.departMinutes,
    durationMinutes: 0
  }))

  // Pinned stops first, so their cars are theirs before the day fills up.
  const pinOf = job => pinFor(cfg, job)
  const queue = placeable.slice().sort((a, b) =>
    (pinOf(b) ? 1 : 0) - (pinOf(a) ? 1 : 0) || difficulty(b) - difficulty(a))

  queue.forEach(job => {
    let best = null
    let bestRoute = null
    // Reasons collected across every attempt, so a job that fits nowhere can
    // say WHY rather than just "no".
    const reasons = {}

    // Every position on every candidate car; the cheapest that works wins.
    const tryRoutes = candidates => candidates.forEach(route => {
      const current = route.stops.map(s => s.job)
      for (let p = 0; p <= current.length; p++) {
        const order = current.slice()
        order.splice(p, 0, job)
        const sim = simulate(route.car, order, cfg)
        if (!sim.ok) { reasons[sim.reason] = true; continue }
        // Marginal cost, not total: a route that is already expensive should not
        // be penalised for work it is already doing.
        const delta = sim.cost - route.cost
        if (!best || delta < best.delta) {
          best = { delta: delta, sim: sim }
          bestRoute = route
        }
      }
    })

    const plate = pinOf(job)
    const pinned = plate ? routes.filter(r => r.car.regNum === plate) : []
    tryRoutes(pinned.length ? pinned : routes)
    if (plate) {
      // Held, or refused with the reason -- and then placed as if unpinned.
      job.pin = { regNum: plate, held: !!best }
      if (!best) {
        job.pin.reason = !pinned.length ? 'NO_CAR'
          : reasons.WINDOW ? 'WINDOW' : reasons.RANGE ? 'RANGE'
            : reasons.TOO_LATE ? 'TOO_LATE' : 'NO_CAR'
        tryRoutes(routes)
      }
    }

    if (best) {
      Object.assign(bestRoute, best.sim)
    } else {
      const reason = reasons.WINDOW ? 'WINDOW' : reasons.RANGE ? 'RANGE'
        : reasons.TOO_LATE ? 'TOO_LATE' : 'NO_CAR'
      unassigned.push({ job: job, reason: reason, message: UNASSIGNED_TH[reason] })
    }
  })

  // Every route, including the empty ones. Filtering empties out here was a bug:
  // it left an unused car unable to RECEIVE relocated work, so a cheap idle
  // vehicle stayed idle while dearer ones ran long.
  const passes = improve(routes, cfg)
  searchWider(routes, unassigned, cfg)

  // Last, and deliberately so: this changes when a driver leaves, never
  // who goes where. Doing it during construction would price every
  // insertion against a departure that the next insertion moves.
  routes.forEach(route => settleDeparture(route, cfg))

  // The fleet rides along: ย้ายไปรถ offers every small car, used or not.
  return Object.assign(report(routes, unassigned, cfg, passes), { cars: fleet })
}

function report (routes, unassigned, cfg, passes) {
  const used = routes.filter(r => r.stops.length)

  // Cost per job, used only to flag the outliers staff should look at -- the
  // lone far-east run that consumes most of a car-day for one installation.
  const perJob = []
  used.forEach(r => r.stops.forEach(() => perJob.push(r.fuelCost / r.stops.length)))
  perJob.sort((a, b) => a - b)
  const median = perJob.length ? perJob[Math.floor(perJob.length / 2)] : 0

  used.forEach(route => {
    route.stops.forEach(stop => {
      const share = route.fuelCost / route.stops.length
      stop.flags = []
      if (median > 0 && share > median * cfg.expensiveStopFactor) stop.flags.push('EXPENSIVE')
      if (stop.job.precision === 'zipcode') stop.flags.push('COARSE_LOCATION')
      // No readable pin. The plan can still route by ตำบล, but the driver's
      // นำทาง then hands Google the free-text address, which can land on the
      // wrong ซอย -- so staff are told while there is still time to phone.
      if (stop.job.precision !== 'exact') stop.flags.push('NO_PIN')
      if (stop.waitMinutes > 30) stop.flags.push('LONG_WAIT')
      // The duration of this stop was assumed, not counted. Every minute after
      // it in the route inherits that uncertainty, so it is worth showing.
      if (stop.job.itemSource === 'default') stop.flags.push('ITEMS_UNKNOWN')
      else if (!stop.job.itemsConfident) stop.flags.push('ITEMS_ESTIMATED')
      // The pin and the ตำบล disagree. The plan follows the pin, so this says
      // which of the two the driver is actually being sent to.
      //
      // The threshold is the point: `pinMismatchKm` is non-null for EVERY pinned
      // booking, because it is a measurement, not a verdict. Testing it for
      // presence rather than size flagged every pinned stop in the day.
      if (stop.job.pinMismatchKm != null &&
          stop.job.pinMismatchKm > cfg.pinMismatchKm) stop.flags.push('PIN_MISMATCH')
      // Several bookings delivered as one stop -- said, so nobody reads the
      // round as a job short.
      if (stop.job.bookings && stop.job.bookings.length > 1) stop.flags.push('SAME_VISIT')
      // The same customer in two slots that do not overlap: two trips to one
      // door, worth a phone call to move one of them.
      if (stop.job.splitVisit) stop.flags.push('SPLIT_VISIT')
      // Moved by hand, or asked to be and refused -- both worth seeing, and
      // both carried into booking.planFlags for the grid.
      if (stop.job.pin) stop.flags.push(stop.job.pin.held ? 'MOVED' : 'MOVE_REFUSED')
    })
  })

  const km = used.reduce((a, r) => a + r.km, 0)
  const fuel = used.reduce((a, r) => a + r.fuelCost, 0)
  const jobs = used.reduce((a, r) => a + r.stops.length, 0)
  const bookingsOf = s => (s.job.bookings ? s.job.bookings.length : 1)

  return {
    routes: used,
    unassigned: unassigned,
    config: cfg,
    summary: {
      cars: used.length,
      // Stops, which is what a car's day is made of. `bookings` is what they
      // deliver, and differs from it by one for every SO that shares a stop.
      jobs: jobs,
      bookings: used.reduce((a, r) => a + r.stops.reduce((b, s) => b + bookingsOf(s), 0), 0),
      mergedVisits: used.reduce((a, r) => a + r.stops.filter(s => bookingsOf(s) > 1).length, 0),
      unassigned: unassigned.length,
      km: km,
      fuelCost: fuel,
      // Estimated, from the working day above. Shown beside fuel so the two
      // plans are compared on what they cost, not on fuel alone.
      overtimeCost: used.reduce((a, r) => a + (r.overtimeCost || 0), 0),
      overtimeMinutes: used.reduce((a, r) => a + (r.overtimeMinutes || 0), 0),
      costPerJob: jobs ? fuel / jobs : 0,
      // EV charges only. A refuel is fifteen minutes at any station and carries
      // none of a charge's queue risk, so counting the two together made a day
      // of routine NGV stops read as a day of charging.
      charges: used.reduce((a, r) => a + r.charges.filter(c => c.kind !== 'refuel').length, 0),
      refuels: used.reduce((a, r) => a + r.charges.filter(c => c.kind === 'refuel').length, 0),
      // Charges taken over the lunch break -- the least risky kind, since the
      // crew is stopped anyway and a queue eats into lunch before it reaches
      // the next customer. Shown beside the count so the choice is informed.
      chargesAtLunch: used.reduce((a, r) =>
        a + r.charges.filter(c => c.kind !== 'refuel' && c.absorbed).length, 0),
      // How much of the plan rests on a counted item total rather than an
      // assumed one. A day at 0% is a timetable of guesses that happens to look
      // precise, and the preview should say so before staff commit it.
      //
      // Keyed on installSource, not itemSource, and the difference is the whole
      // point: once staff set a duration by hand the timetable stops resting on
      // the count, so the job leaves these three buckets entirely and lands in
      // itemsManual. Counting it as assumed would warn about a guess nothing
      // uses, which is how staff learn to ignore the warning that matters.
      itemsCounted: used.reduce((a, r) =>
        a + r.stops.filter(s => s.job.installSource === 'explicit').length, 0),
      itemsEstimated: used.reduce((a, r) =>
        a + r.stops.filter(s => s.job.installSource === 'productList').length, 0),
      itemsAssumed: used.reduce((a, r) =>
        a + r.stops.filter(s => s.job.installSource === 'default').length, 0),
      itemsManual: used.reduce((a, r) =>
        a + r.stops.filter(s => s.job.installSource === 'manual').length, 0),
      // Routed stops the driver will navigate to by address rather than by pin.
      noPin: used.reduce((a, r) =>
        a + r.stops.filter(s => s.job.precision !== 'exact').length, 0),
      latestReturn: used.reduce((a, r) => Math.max(a, r.endAt), cfg.departMinutes),
      // Paid minutes the day no longer spends waiting for its first window
      // to open. Small, and honest about being small.
      idleTrimmed: used.reduce((a, r) => a + (r.idleTrimmed || 0), 0),
      improvePasses: passes
    }
  }
}

// Renders a plan as the table staff read. Kept here rather than in a view so
// check-dispatch.js can assert what they will actually see.
function describePlan (plan) {
  const hm = travelTime.toClock
  const lines = []
  plan.routes.forEach(route => {
    lines.push(`${route.car.regNum} (${route.car.fuelType}, ฿${route.car.costPerKm}/กม.)  ` +
      `${route.stops.length} งาน  ${route.km.toFixed(0)} กม.  ฿${route.fuelCost.toFixed(0)}  ` +
      `${hm(route.startAt)}-${hm(route.endAt)}`)
    route.stops.forEach((s, i) => {
      const loc = R.parseLocation(s.job.booking.location)
      const where = loc ? loc.subDt : (s.job.booking.name || '?')
      const more = s.job.bookings && s.job.bookings.length > 1 ? ` (${s.job.bookings.length} การจอง)` : ''
      lines.push(`   ${i + 1}. ${hm(s.arriveAt)} ถึง / ${hm(s.startAt)} เริ่ม / ${hm(s.departAt)} ออก  ` +
        `${where}${more}  ${s.job.items} ชิ้น  ${s.legKm.toFixed(1)} กม.` +
        (s.charge ? `  [ชาร์จ ${s.charge.name} +${s.charge.addedMinutes} นาที]` : '') +
        (s.flags && s.flags.length ? `  [${s.flags.join(',')}]` : ''))
    })
  })
  plan.unassigned.forEach(u => {
    lines.push(`ยังไม่ได้จัด: ${u.job.booking.name || u.job.id || '?'} — ${u.message}`)
  })
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// Turning a plan into what carBooking.vue already reads
// ---------------------------------------------------------------------------
//
// carBooking.vue renders the `booking` collection as a grid of car x day x slot.
// Its documents carry `carName` (the plate), `date`, `slot` ('AM' | 'PM'),
// `totalTime`, `totalVolume` and the zone fields. Writing that exact shape is
// what lets the auto-planner appear in the tool the delivery team already uses
// rather than in a second screen beside it.
//
// Three fields exist only for this planner and are the whole safety story:
//
//   autoAssigned  true, and ONLY ever true on documents this planner wrote.
//                 commitPlan touches nothing without it, so an appointment the
//                 delivery team made by hand can never be moved or deleted by a
//                 re-plan.
//   sourceSlotId  the bookingSlot document this came from. Re-planning a day
//                 replaces by this key rather than appending, so pressing the
//                 button twice cannot double-book a customer.
//   planVersion   which run produced it, so a half-finished commit is visible
//                 rather than silently mixed with the previous plan.

// carBooking has no ทั้งวัน slot -- only AM and PM. Rather than mapping the
// booking's SLOT, this reads the hour the planner actually scheduled the stop
// at, which is the truer answer: a ทั้งวัน job worked at 09:00 belongs in the
// morning column, and the planner is the only thing that knows it went there.
function slotForStop (stop) {
  return (stop.startAt % 1440) < 720 ? 'AM' : 'PM'
}

// `zoneOf` maps a "ตำบล,อำเภอ,จังหวัด" string to { zoneMain, zoneDv }. Passed in
// rather than imported so this module stays free of zonedv.json, which is 1.8 MB.
// Everything a `booking` document carries ABOUT THE CUSTOMER AND THE JOB, in one
// place, because there are now two hops into that collection: this planner's
// routed stops, and an out-of-area route sent to จัดรถ by hand.
//
// One copy on purpose. `geo` and `landmark` have been dropped at a hop twice in
// this codebase, each time by someone writing the field list out again and
// leaving two of them off, and each time the failure was silent -- the driver
// simply got a district centroid and นำทาง handed Google the address text. A
// second hand-written copy of this list is how it happens a third time.
//
// Nothing about the ROUTE belongs here: no stopOrder, no ETA, no planKm. The
// trip path has no route, and an etaArrive written for a stop nobody sequenced
// is a number staff would read as a promise.
// ---------------------------------------------------------------------------
// Out-of-area routes
// ---------------------------------------------------------------------------
//
// A route (outOfAreaTrip) holds one truck: that is what the booking pages
// charge it, so the planner never mixes its stops into a Bangkok round or
// splits it across cars. What it does is the part delivery did by hand --
// which small car, in what order, back when.
//
// Bangkok first: planTrips runs on a finished plan and offers each route only
// the cars that plan left unused, so a route can never make the Bangkok day
// worse. When none fits it says which car WOULD, and a person decides.
//
// Far ตำบล are placed through geoPointsWide.json, which the planner's worker
// merges into geo.js (geo.addPoints). Every leg is timed on the highway
// profile (travelTime.HIGHWAY_SPEEDS, cfg.highway), not the city's speed ramp
// and rush bands, which put 2026-10-07's routes 40-75% over Google.

// Exact search up to this many stops (7! = 5040 orders per car). A route is
// rarely more than five; past seven, nearest-first plus 2-opt.
const TRIP_EXACT_MAX = 7

const TRIP_REASON_TH = {
  UNPLACEABLE: 'ต้องปักพิกัดหรือระบุตำบลก่อน',
  NO_FREE_CAR: 'ไม่มีรถว่างที่วิ่งถึง',
  CAR_BUSY: 'รถคันนี้มีรอบในเมืองวันนี้แล้ว',
  WINDOW: 'ไปไม่ทันช่วงเวลาที่ลูกค้าเลือก',
  RANGE: 'ระยะทางเกินที่รถวิ่งได้ แม้เติม/ชาร์จระหว่างทาง',
  TOO_LATE: 'กลับถึงคลังเกิน 19:00',
  NO_CAR: 'ไม่มีรถที่ใช้ได้',
  TWO_DAY_FAILED: 'ไม่พอแม้แบ่ง 2 วัน',
  DAY1_LATE: 'วันที่ 1 ส่งเสร็จเกิน 18:00',
  SPLIT: 'แบ่งวันตรงนี้ไม่ได้'
}

function permutations (items) {
  if (items.length <= 1) return [items.slice()]
  const out = []
  items.forEach((x, i) => {
    permutations(items.slice(0, i).concat(items.slice(i + 1))).forEach(p => out.push([x].concat(p)))
  })
  return out
}

// Two days were tried only because one day failed, so their failure is the
// answer worth giving.
const reasonFrom = reasons => reasons.TWO_DAY_FAILED ? 'TWO_DAY_FAILED'
  : reasons.WINDOW ? 'WINDOW' : reasons.RANGE ? 'RANGE'
  : reasons.TOO_LATE ? 'TOO_LATE' : 'NO_CAR'

// Nearest stop next, from the depot: the starting order for a route too long
// to try every order of.
function nearestOrder (jobs) {
  const order = []
  const left = jobs.slice()
  let here = geo.DEPOT
  while (left.length) {
    left.sort((a, b) => geo.haversineKm(here, a.point) - geo.haversineKm(here, b.point))
    const next = left.shift()
    order.push(next)
    here = next.point
  }
  return order
}

// The cheapest order of `jobs` on `car`: { ok: true, sim } or { ok: false, reasons }.
function bestOrder (car, jobs, cfg) {
  const reasons = {}
  let best = null
  const consider = sim => {
    if (!sim.ok) { reasons[sim.reason] = true; return }
    if (!best || sim.cost < best.cost - 1e-9) best = sim
  }
  if (jobs.length <= TRIP_EXACT_MAX) {
    permutations(jobs).forEach(order => consider(simulate(car, order, cfg)))
  } else {
    consider(reorder(car, nearestOrder(jobs), cfg))
  }
  return best ? { ok: true, sim: best } : { ok: false, reasons: reasons }
}

// The cheapest two-day version of a route on `car`: every order (exact up to
// TRIP_EXACT_MAX, else the nearest-stop order), each with the LARGEST split
// whose two days both work -- the most work on day 1. Run only when no one-day
// order fits (tripOnCars).
function bestTwoDay (car, jobs, cfg) {
  let best = null
  // simulateTwoDay's memo keys do not name the car, so the memo is per car.
  const memo = {}
  const orders = jobs.length <= TRIP_EXACT_MAX ? permutations(jobs) : [nearestOrder(jobs)]
  orders.forEach(order => {
    for (let split = order.length - 1; split >= 1; split--) {
      const sim = simulateTwoDay(car, order, cfg, split, memo)
      if (!sim.ok) continue
      if (!best || sim.cost < best.cost - 1e-9) best = sim
      return
    }
  })
  return best ? { ok: true, sim: best } : { ok: false, reasons: { TWO_DAY_FAILED: true } }
}

// The two configs a route is tried with: highway legs, first without a charge
// and then with one -- the delivery team's rule, as planDay keeps it. The
// charge allowance is restored from DEFAULTS because a minCharge plan's own
// config carries maxChargesPerDay 0.
function tripPasses (cfg) {
  // highway: every leg of a route is between provinces (travelTime
  // HIGHWAY_SPEEDS), the depot's own leg included -- ไทรน้อย is already out of
  // the city's traffic on the way north and west.
  return [
    Object.assign({}, cfg, { maxChargesPerDay: 0, highway: true }),
    Object.assign({}, cfg, { maxChargesPerDay: Math.max(cfg.maxChargesPerDay || 0, DEFAULTS.maxChargesPerDay), highway: true })
  ]
}

// The cheapest (car, order) among `cars`, in one day if any car can; over two
// days only when none can (a route that fits one day is planned as before).
//
// `orders`, when given, remembers each car's best order per pass: it depends
// only on the route, the car and the config, not on which other cars are
// taken -- so ย้ายไปรถ, which asks the same routes with a different car taken
// each time, searches each (route, car) once.
function tripOnCars (jobs, cars, cfg, orders, tripId) {
  const passes = tripPasses(cfg)
  const reasons = {}
  const search = (find, tag) => {
    for (let p = 0; p < passes.length; p++) {
      let best = null
      cars.forEach(car => {
        const k = tripId + '|' + car.regNum + '|' + p + '|' + tag
        const r = orders && orders[k] ? orders[k] : find(car, jobs, passes[p])
        if (orders) orders[k] = r
        if (!r.ok) { Object.assign(reasons, r.reasons); return }
        if (!best || r.sim.cost < best.sim.cost - 1e-9) best = { sim: r.sim, cfg: passes[p] }
      })
      if (best) return { ok: true, sim: best.sim, cfg: best.cfg }
    }
    return null
  }
  return search(bestOrder, '1') || search(bestTwoDay, '2') || { ok: false, reasons: reasons }
}

// A route's visits as jobs, or the bookings that cannot be placed.
function tripJobs (group) {
  const base = { tripId: group.tripId, provinces: group.provinces, bookingIds: group.rows.map(b => b.id) }
  const jobs = group.visits.map(toVisitJob)
  const unplaced = []
  jobs.forEach(j => {
    if (!j.point || !j.point.precision) (j.bookings || [j.booking]).forEach(b => unplaced.push(b && b.id))
  })
  return { base: base, jobs: jobs, unplaced: unplaced }
}

function tripFailed (base, reason, extra) {
  return Object.assign(base, { ok: false, reason: reason, message: TRIP_REASON_TH[reason] }, extra || {})
}

function tripDone (base, got) {
  const route = Object.assign({}, got.sim)
  // settleDeparture re-simulates ONE day; a two-day route keeps the departure
  // simulateTwoDay chose.
  if (!route.days) settleDeparture(route, got.cfg)
  return Object.assign(base, route, { ok: true })
}

const byCostPerKm = (a, b) => (a.costPerKm || 0) - (b.costPerKm || 0)

// TRANSITION PERIOD. Some days are still dispatched by hand on คิวรถเล็ก, and
// on 2026-10-07 nineteen of the day's twenty-one SOs were already there when
// the planner offered to write them again. A booking whose SO is in that day's
// deliveryList, or in a `booking` job typed in by hand (no sourceSlotId and
// not this planner's), is a conflict: ยืนยันแผนนี้ and ส่งเข้าคิวจัดรถ refuse
// while one stands, and staff clear it on คิวรถเล็ก or keep the day manual.
// Matched by SO -- a นัดหมาย has none, and คิวรถเล็ก cannot confirm one without.
// A pickup (no SO, an RT) is matched by its RT instead: 'rt:' keys never meet
// 'so:' keys, so an exchange is still matched by its SO alone.
// Delete this, its two callers in dispatchService and the red card on
// dispatchPlan.vue once nobody dispatches a planned day by hand.
// -> [{ id, soNo, name, regNum, where: 'deliveryList' | 'booking', tripId }]
//    (a pickup's entry has soNo '' and carries rtNo)
function dispatchedConflicts (bookings, listRows, bookingDocs) {
  const no = v => (v == null || v === '') ? null : String(v)
  const keyOf = x => {
    if (!x) return null
    const so = no(x.soNo)
    if (so) return 'so:' + so
    const rt = no(x.rtNo)
    return rt ? 'rt:' + rt : null
  }
  const taken = {}
  ;(listRows || []).forEach(r => {
    const k = keyOf(r)
    if (k && !taken[k]) taken[k] = { regNum: r.regNum || '', where: 'deliveryList' }
  })
  ;(bookingDocs || []).forEach(d => {
    const k = keyOf(d)
    if (!k || taken[k] || d.autoAssigned === true || d.sourceSlotId) return
    taken[k] = { regNum: d.carName || '', where: 'booking' }
  })
  const out = []
  ;(bookings || []).forEach(b => {
    const k = keyOf(b)
    if (!k || !taken[k]) return
    const hit = { id: b.id, soNo: no(b.soNo) || '', name: b.name || b.custName || '', regNum: taken[k].regNum, where: taken[k].where, tripId: b.tripId || null }
    if (k.indexOf('rt:') === 0) hit.rtNo = no(b.rtNo)
    out.push(hit)
  })
  return out
}

// The routes already sent to คิวรถเล็ก, tripId -> plate: sendTripToDispatch
// stamps each booking it sends with the plate and sentToDispatchAt (the board
// on จองวันส่งสินค้า sends the same way). A sent route holds its car.
function sentPlates (setAside) {
  const out = {}
  ;(setAside || []).forEach(b => {
    if (b && b.tripId && b.sentToDispatchAt && b.regNum && !out[b.tripId]) out[b.tripId] = b.regNum
  })
  return out
}

// The later days of each multi-day route already sent to คิวรถเล็ก (`days` 2,
// set when sending): on those days the car is still out on the route.
// -> [{ date, regNum, tripId, provinces }]
function routeDayHolds (routes) {
  const out = []
  ;(routes || []).forEach(r => {
    if (!r || r.cancelled || !r.sentAt || !r.regNum || !r.date) return
    const days = Number(r.days) || 1
    for (let i = 1; i < days; i++) out.push({ date: DR.addDays(r.date, i), regNum: r.regNum, tripId: r.id, provinces: r.provinces || [] })
  })
  return out
}

// The plates a multi-day route holds on `date` (not its first day -- that is
// sentPlates' job, read off the day's own bookings).
function carriedPlates (routes, date) {
  return routeDayHolds(routes).filter(h => h.date === date).map(h => h.regNum)
}

// ยกเลิกการส่ง for one route, decided before anything is written. `rows` are
// the route's bookingSlot rows, `sentDocs` the `booking` jobs of that date,
// `listRows` the deliveryList rows of that date.
//
// คิวรถเล็ก's confirm creates the deliveryList row and DELETES the booking
// job, so a sent booking whose job is gone was either confirmed or deleted by
// hand, and only deliveryList can tell which. Confirmed refuses the whole
// route: that job may be in the driver's day, and deleting it belongs on
// คิวรถเล็ก, where its stamp warnings are. A pickup (a return alone) has no SO
// and is looked for by its RT, which its confirmed row carries; a booking with
// neither is not guessed at.
// -> { ok, deleteIds, clearSlotIds, blocked: [{ id, name, soNo, rtNo?, why }] }
function unsendPlan (rows, sentDocs, listRows) {
  const sent = (rows || []).filter(b => b && b.sentToDispatchAt)
  const jobOf = {}
  ;(sentDocs || []).forEach(d => { if (d && d.sourceSlotId) jobOf[d.sourceSlotId] = d.id })
  const confirmedSo = {}
  const confirmedRt = {}
  ;(listRows || []).forEach(r => {
    if (r && r.soNo != null && r.soNo !== '') confirmedSo[String(r.soNo)] = true
    if (r && r.rtNo != null && r.rtNo !== '') confirmedRt[String(r.rtNo)] = true
  })

  const deleteIds = []
  const blocked = []
  sent.forEach(b => {
    if (jobOf[b.id]) { deleteIds.push(jobOf[b.id]); return }
    const name = b.name || b.custName || ''
    const so = b.soNo != null && b.soNo !== '' ? String(b.soNo) : null
    const rt = !so && b.rtNo != null && b.rtNo !== '' ? String(b.rtNo) : null
    if (so) { if (confirmedSo[so]) blocked.push({ id: b.id, name: name, soNo: so, why: 'CONFIRMED' }) }
    else if (rt) { if (confirmedRt[rt]) blocked.push({ id: b.id, name: name, soNo: null, rtNo: rt, why: 'CONFIRMED' }) }
    else blocked.push({ id: b.id, name: name, soNo: null, why: 'UNKNOWN' })
  })
  return {
    ok: sent.length > 0 && !blocked.length,
    deleteIds: blocked.length ? [] : deleteIds,
    clearSlotIds: blocked.length ? [] : sent.map(b => b.id),
    blocked: blocked
  }
}

// Answers already worked out, by set-aside list and config, then by the fleet
// and the cars the plan leaves taken -- the only things the answer depends
// on. ย้ายไปรถ asks once per car, mostly with the same leftover cars, and a
// route that cannot be planned costs the full search every time (~1 s for
// seven stops). Weak keys: a list and a config live as long as their plan.
const TRIP_MEMO = new WeakMap()

// -> one tripPlan per route with a tripId, in groupSetAside's order. A car
// given to one route is not offered to the next.
function planTrips (setAside, cars, plan, cfg) {
  const busy = {}
  ;((plan && plan.routes) || []).forEach(r => { if (r.stops && r.stops.length) busy[r.car.regNum] = true })
  const list = setAside || []
  const key = (cars || []).map(c => c && c.regNum).join(',') + '|' + Object.keys(busy).sort().join(',')
  let byCfg = TRIP_MEMO.get(list)
  if (!byCfg) { byCfg = new WeakMap(); TRIP_MEMO.set(list, byCfg) }
  let byKey = byCfg.get(cfg)
  if (!byKey) { byKey = { orders: {} }; byCfg.set(cfg, byKey) }
  if (!byKey[key]) byKey[key] = planTripsNow(list, cars, busy, cfg, byKey.orders)
  return byKey[key]
}

function planTripsNow (setAside, cars, busy, cfg, orders) {
  const small = (cars || []).filter(c => c && c.size !== 'large').slice().sort(byCostPerKm)
  // Who has each car: 'bangkok' (a round in the plan) or 'route' (an earlier
  // route here) -- what NO_FREE_CAR tells the person to go and look at.
  const taken = {}
  Object.keys(busy).forEach(plate => { taken[plate] = 'bangkok' })
  // Sent routes first: their cars are theirs before any other route looks.
  const held = sentPlates(setAside)
  Object.keys(held).forEach(tripId => { taken[held[tripId]] = 'route' })

  return groupSetAside(setAside).filter(g => g.tripId).map(group => {
    const t = tripJobs(group)
    const plate = held[group.tripId]
    if (plate) {
      // Sent: the route is on that car, whatever would be cheaper now. Timed
      // on it so a booking that joined late gets an order and ETAs too. Any
      // car in the fleet -- a truck may have been chosen by hand.
      const sent = {
        regNum: plate,
        count: group.rows.filter(b => b.sentToDispatchAt && b.regNum === plate).length,
        total: group.rows.length
      }
      const car = (cars || []).find(c => c && c.regNum === plate)
      if (t.unplaced.length) return tripFailed(t.base, 'UNPLACEABLE', { unplaced: t.unplaced, sent: sent })
      if (!car) return tripFailed(t.base, 'NO_CAR', { sent: sent })
      const onIt = tripOnCars(t.jobs, [car], cfg, orders, group.tripId)
      return onIt.ok
        ? Object.assign(tripDone(t.base, onIt), { sent: sent })
        : tripFailed(t.base, reasonFrom(onIt.reasons), { sent: sent })
    }
    if (t.unplaced.length) return tripFailed(t.base, 'UNPLACEABLE', { unplaced: t.unplaced })
    if (!small.length) return tripFailed(t.base, 'NO_CAR')

    const free = small.filter(c => !taken[c.regNum])
    const got = free.length ? tripOnCars(t.jobs, free, cfg, orders, group.tripId) : { ok: false, reasons: {} }
    if (got.ok) {
      taken[got.sim.car.regNum] = 'route'
      return tripDone(t.base, got)
    }
    // Only the taken cars: every free one has just failed already.
    const others = small.filter(c => taken[c.regNum])
    const any = others.length ? tripOnCars(t.jobs, others, cfg, orders, group.tripId) : { ok: false, reasons: {} }
    if (any.ok) {
      return tripFailed(t.base, 'NO_FREE_CAR', {
        wouldFit: {
          regNum: any.sim.car.regNum,
          usedBy: taken[any.sim.car.regNum],
          endAt: any.sim.endAt,
          fuelCost: any.sim.fuelCost,
          km: any.sim.km,
          days: any.sim.days || 1
        }
      })
    }
    return tripFailed(t.base, reasonFrom(Object.assign({}, got.reasons, any.reasons)))
  })
}

// One route on one named car -- the send dialog, when the person sending picks
// another plate than the suggestion. Any car in the fleet, large included: by
// hand is how a truck has always gone out of area.
function tripForCar (plan, tripId, regNum) {
  const group = groupSetAside((plan && plan.setAside) || []).find(g => g.tripId === tripId)
  const car = ((plan && plan.cars) || []).find(c => c && c.regNum === regNum)
  if (!group || !car) return tripFailed({ tripId: tripId, provinces: [], bookingIds: [] }, 'NO_CAR')
  const t = tripJobs(group)
  // A car with a Bangkok round that day is not free from the morning, and
  // timing the route as if it were would write ETAs nobody will keep. It may
  // still be sent -- a person can know better -- but with no times.
  const onRound = ((plan && plan.routes) || []).some(r => r.car && r.car.regNum === regNum && r.stops && r.stops.length)
  if (onRound) return tripFailed(t.base, 'CAR_BUSY')
  if (t.unplaced.length) return tripFailed(t.base, 'UNPLACEABLE', { unplaced: t.unplaced })
  const got = tripOnCars(t.jobs, [car], (plan && plan.config) || config())
  return got.ok ? tripDone(t.base, got) : tripFailed(t.base, reasonFrom(got.reasons))
}

// The send dialog's ▲/▼: a two-day route with day 1 ending after `split`
// stops, on the same car in the same order. Tried with the same two passes as
// planning (no charge, then one). -> a trip plan, or tripFailed with the reason
// that split cannot work.
function retimeTripSplit (plan, trip, split) {
  const t = trip || {}
  const base = { tripId: t.tripId, provinces: t.provinces || [], bookingIds: t.bookingIds || [] }
  if (!t.ok || !t.car || !t.stops) return tripFailed(base, 'NO_CAR')
  const jobs = t.stops.map(s => s.job)
  const passes = tripPasses((plan && plan.config) || config())
  let reason = 'SPLIT'
  for (let p = 0; p < passes.length; p++) {
    const sim = simulateTwoDay(t.car, jobs, passes[p], split)
    if (sim.ok) return tripDone(base, { sim: sim, cfg: passes[p] })
    reason = sim.reason
  }
  return tripFailed(base, reason)
}

// Does this visit (its bookings) carry a pin a driver can navigate to? Any one
// readable pin is enough -- driverDay.groupStops takes the first that holds
// one -- so the ไม่มีพิกัด chip and the driver's นำทาง cannot disagree.
function visitPinned (bookings) {
  return (bookings || []).some(b => !!(b && geo.parseGeo(b.geo)))
}

// A pin saved on the planning page, for a route already sent to คิวรถเล็ก:
// those `booking` documents hold their own copy (bookingDocFrom), and would
// keep the blank one. -> [{ id, data }] for each document of the visit whose
// pin or landmark differs; deliveryList is not touched -- past คิวรถเล็ก's
// confirm, its พิกัด dialog is the place.
function sentPinPatches (bookingDocs, slotIds, fields) {
  const ids = slotIds || []
  const geoValue = (fields && fields.geo) || ''
  const landmark = (fields && fields.landmark) || ''
  return (bookingDocs || [])
    .filter(d => d && ids.indexOf(d.sourceSlotId) !== -1)
    .filter(d => (d.geo || '') !== geoValue || (d.landmark || '') !== landmark)
    .map(d => ({ id: d.id, data: { geo: geoValue, landmark: landmark } }))
}

// Set-aside bookings, gathered back into the routes they sit on.
//
// loadDayBookings returns them flat, because that is how it reads them. But
// sending them to จัดรถ is a per-ROUTE act -- one route, one truck, one plate --
// so the screen that offers that button has to see routes, not a list. Bookings
// with no tripId are the strays PR #35 left behind: they group under null and
// have nothing to send them on until someone attaches them to a route.
//
// Provinces are read off the bookings rather than the trip document, because
// this runs on a plan result that never loaded one -- and what the bookings say
// is what the operator is looking at.
//
// `visits` is `rows` gathered by groupVisits, the rule the routed stops use:
// the same customer's SOs on one route are one door, and the card lists them
// as one. Display only -- a route goes to จัดรถ whole, on one plate, so its
// SOs cannot be split across trucks whatever this says.
//
// -> [{ tripId, provinces: [...], rows: [...], visits: [[...], ...] }], routes
// first and strays last.
function groupSetAside (setAside, provinceOf) {
  const nameOf = provinceOf || (b => (String(b.location || '').split(',')[2] || ''))
  const byTrip = {}
  const order = []
  ;(setAside || []).forEach(b => {
    const key = b.tripId || ''
    if (!byTrip[key]) { byTrip[key] = { tripId: b.tripId || null, provinces: [], rows: [] }; order.push(key) }
    byTrip[key].rows.push(b)
    const province = nameOf(b)
    if (province && byTrip[key].provinces.indexOf(province) === -1) byTrip[key].provinces.push(province)
  })
  return order
    .map(key => {
      byTrip[key].provinces.sort((a, b) => a.localeCompare(b))
      byTrip[key].visits = groupVisits(byTrip[key].rows)
      return byTrip[key]
    })
    // Strays last: they are the group nothing can be done about from here.
    .sort((a, b) => {
      if (!a.tripId) return b.tripId ? 1 : 0
      if (!b.tripId) return -1
      return (a.provinces[0] || '').localeCompare(b.provinces[0] || '')
    })
}

// carBooking's day grid buckets bookings into AM / PM / OM. `bookingSlot` says
// am / pm / full. Two vocabularies for one idea, and the document written here
// is read by that grid -- so it speaks the grid's.
//
// This is not cosmetic. processBookingChanges does `daySlot[slot].push(booking)`
// and an unknown key is undefined, so the push THROWS -- and the throw aborts
// the whole snapshot handler. One booking carrying a lowercase slot made the
// entire day render empty, including every booking that was fine. The symptom
// is "the job I just sent is not there", which points at the write, and the
// write was correct.
//
// slotForStop already returns AM/PM, so the planner's path passes through
// unchanged.
function toCarSlot (slot) {
  const s = String(slot || '').trim()
  if (s === 'AM' || s === 'PM' || s === 'OM') return s
  if (s === 'am') return 'AM'
  if (s === 'pm') return 'PM'
  // ทั้งวัน, and anything unrecognised. carBooking's own getTimeSlot falls back
  // to OM for a booking with no time, which is the same bucket for the same
  // reason: it belongs to neither half of the day.
  return 'OM'
}

function bookingDocFrom (booking, meta) {
  const b = booking || {}
  const m = meta || {}
  const zoneOf = m.zoneOf || (() => ({ zoneMain: null, zoneDv: [] }))
  const zone = zoneOf(b.location) || {}

  return {
    // The fields carBooking.vue renders.
    name: b.name || b.custName || '',
    telephone: b.telephone || b.custTel || '',
    address: b.address || b.custAddress || '',
    // The พิกัด staff dropped after asking the customer. It lives on the booking
    // (SAP's U_KSK_Geolocation only seeds it when the SO is looked up) -- and this planner was the
    // first of two hops that dropped it, which is why an auto-assigned job could
    // never navigate to anything but its address text. Empty string rather than
    // undefined: Firestore refuses undefined outright.
    geo: b.geo || '',
    // จุดสังเกต, dropped at the same two hops and filled in more often than the
    // pin. For an address Google reads wrongly it is the thing that actually
    // gets the driver to the door.
    landmark: b.landmark || '',
    location: b.location || '',
    zipcode: b.zipcode || '',
    zoneMain: zone.zoneMain != null ? zone.zoneMain : null,
    zoneDv: zone.zoneDv || [],
    carName: m.carName || '',
    date: m.date || b.date || '',
    slot: toCarSlot(m.slot || b.slot),
    // Minutes of installation, which is what the grid totals.
    totalTime: m.totalTime != null ? m.totalTime : (b.installMinutes || null),
    // Both carried from the booking rather than invented here. An SO booking
    // stores the product lines GetProdList returned, and carBooking's
    // appointment table renders รหัสสินค้า, สีหน้าบาน|โครงตู้ and จำนวน straight
    // out of them -- an appointment that dropped them showed a row of dashes
    // where a manual one shows the order. Volume is whatever those lines add up
    // to, and 0 when there are none: bookingSlot has no cubic measure of its
    // own, and a fabricated figure would total up into one staff would believe.
    totalVolume: Number(b.totalVolume) || 0,
    prodList: b.prodList || [],
    // A return rides the same hop as the SO: carBooking's confirm writes
    // prodListRt onto the deliveryList row and calls UpdateRT with rtDocEntry.
    // Empty values rather than undefined -- Firestore refuses undefined.
    rtNo: b.rtNo || '',
    rtDocEntry: b.rtDocEntry || null,
    jobType: b.jobType || 'delivery',
    prodListRt: b.prodListRt || [],
    // null, never []. carBooking's จำนวน and ประเภทสินค้า cells read
    //   v-if="booking.productTypes" ... v-else-if="booking.prodList"
    // and an empty array is truthy in JavaScript, so writing [] took the first
    // branch, rendered nothing for each of its zero entries, and left both
    // columns blank -- not even the "-" the v-else prints. An SO booking's
    // quantities live in prodList[].qty, which is the branch that has to be
    // reached.
    productTypes: (b.productTypes && b.productTypes.length) ? b.productTypes : null,
    soNo: b.soNo || null,
    // The visit this SO shares a stop with, so jobs downstream need not be
    // matched up by phone again. null for an ordinary booking.
    visitId: b.visitId || null,
    sourceSlotId: b.id || null,
    user: m.user || ''
  }
}

// What a SEQUENCED stop adds to a `booking` document: its place in the round
// and its times. One copy for both hops that sequence -- the planner's routed
// stops (toBookingDocs) and an out-of-area route sent with its plan
// (tripStopFields) -- for the same reason bookingDocFrom is one copy.
function routedFields (stop, index) {
  return {
    stopOrder: index + 1,
    etaArrive: travelTime.toClock(stop.arriveAt),
    etaStart: travelTime.toClock(stop.startAt),
    etaDepart: travelTime.toClock(stop.departAt),
    // The appointment's own time column. The planner is the only thing
    // that knows this, and it was computing it and then not writing it.
    time: travelTime.toClock(stop.startAt) + ' - ' + travelTime.toClock(stop.departAt),
    planKm: Number(stop.legKm.toFixed(2)),
    planFlags: stop.flags || [],
    chargeAt: stop.charge ? stop.charge.name : null
  }
}

// A planned out-of-area route's fields for each booking on it, keyed by
// bookingSlot id -- what sendTripToDispatch lays over bookingDocFrom. Every
// member of a merged stop, with the stop's minutes shared between them, as
// toBookingDocs does. {} for a route that could not be planned -- and, when
// `regNum` is given, for a route timed on any other car: times planned for one
// car written under another plate are a promise nobody made.
// A two-day plan (trip.days 2) sent as two days adds each stop's planDate (the
// departure `date` + its day - 1) and planSeq -- what splits จัดรถ, ใบขนส่ง and
// the driver's day. Sent as ONE day it gives nothing: day-2 times on day 1
// would be a promise nobody made either.
// `opts.sentBefore` (the route was already sent when this late booking joins
// it) drops planDate / planSeq: a late booking on a sent two-day route gets no
// day. Its day and number could contradict the jobs already sent, so the driver
// app lists it as unplanned, จัดรถ puts it last, and staff place it.
function tripStopFields (trip, regNum, date, days, opts) {
  const out = {}
  if (!trip || !trip.ok) return out
  if (regNum != null && !(trip.car && trip.car.regNum === regNum)) return out
  const twoDay = trip.days > 1
  if (twoDay && days != null && Number(days) < 2) return out
  const dayOf = twoDay && date && Number(days) > 1 && !(opts && opts.sentBefore === true)
  trip.stops.forEach((stop, index) => {
    const members = stop.job.bookings || [stop.job.booking || {}]
    const minutes = shareMinutes(stop.installMinutes, stop.job.memberMinutes)
    members.forEach((b, k) => {
      if (!b || !b.id) return
      out[b.id] = Object.assign({
        slot: toCarSlot(slotForStop(stop)),
        totalTime: minutes[k] != null ? minutes[k] : stop.installMinutes
      }, routedFields(stop, index), dayOf ? { planDate: DR.addDays(date, (stop.day || 1) - 1), planSeq: index + 1 } : {})
    })
  })
  return out
}

function toBookingDocs (plan, meta) {
  const m = meta || {}
  const docs = []

  plan.routes.forEach(route => {
    route.stops.forEach((stop, index) => {
      // EVERY booking of the stop, not only the one the job is named after.
      // commitPlan deletes each autoAssigned row whose sourceSlotId this list
      // lacks, so writing the primary alone would delete the second SO's job.
      const members = stop.job.bookings || [stop.job.booking || {}]
      const minutes = shareMinutes(stop.installMinutes, stop.job.memberMinutes)
      members.forEach((member, k) => {
        const b = member || {}

        docs.push({
          sourceSlotId: b.id || null,
          doc: Object.assign(bookingDocFrom(b, {
            date: m.date,
            carName: route.car.regNum,
            slot: slotForStop(stop),
            totalTime: minutes[k] != null ? minutes[k] : stop.installMinutes,
            user: m.user,
            zoneOf: m.zoneOf
          }), Object.assign({
            // This planner's own fields, which only a routed stop has.
            autoAssigned: true,
            planVersion: m.planVersion || '',
            // Which of the two offered plans staff committed (see planOptions),
            // so a month of choices can say whether the default is right.
            planMode: plan.mode || null
          }, routedFields(stop, index)))
        })
      })
    })
  })

  return docs
}

// ---------------------------------------------------------------------------
// Opening a round in Google Maps
// ---------------------------------------------------------------------------
//
// The Maps URL API takes an origin, a destination and waypoints, needs no API
// key and costs nothing, and opens in the Google Maps app on a driver's phone.
// That makes it the right tool here: staff get to see the shape of a round, and
// a driver gets something they can actually navigate with.
//
// It is also a free second opinion on this module's own arithmetic. Distance
// here is haversine x 1.35, worth about +/-15%; Google routes on real roads. A
// round whose Maps total is far off what the plan says is worth investigating,
// and the button is the cheapest way to notice.
//
// THE TWO BUTTONS WANT DIFFERENT THINGS, and conflating them was a bug.
//
// The first version used the written address for waypoints, on the reasoning
// that Google geocodes a street address better than a ตำบล centroid
// approximates one. True, and the wrong trade: Google draws NO route at all if
// even one waypoint fails to geocode, so a single unresolvable address turns
// the whole round into a list of pins with no line between them. Thai addresses
// are free text typed by staff, so that is not a rare case.
//
//   แผนที่ (the round)  -> COORDINATES, always. The question is "what shape is
//                          this round", a centroid answers it fine, and a
//                          coordinate can never fail to resolve.
//   the per-stop pin    -> the ADDRESS, because there the question is "where
//                          exactly is this customer", and if it fails the user
//                          simply gets a search result rather than a broken map.

// The Maps URL API accepts at most nine waypoints. No round in this service area
// comes close -- the longest observed is seven stops -- but a day that did would
// otherwise silently lose its last stops, which is exactly the kind of quiet
// truncation a driver discovers at 16:00.
const MAX_WAYPOINTS = 9

// For the round. Always a coordinate, so a waypoint can never be the thing that
// stops Google drawing the line. Falls back through the same chain geo.js uses
// for planning -- pin, then ตำบล, then อำเภอ, then zipcode.
function routeTargetFor (booking) {
  const point = geo.resolveBooking(booking)
  if (!point || !point.precision) return null
  return point.lat + ',' + point.lng
}

// For a single stop, where the question is which doorstep rather than which
// shape. The written address wins over the centroid because Google geocodes a
// street address far better than we can approximate one -- and if it fails here
// the user gets a search result, not a broken route.
function navTargetFor (booking) {
  const b = booking || {}

  const pin = geo.parseGeo(b.geo)
  if (pin) return pin.lat + ',' + pin.lng

  const loc = R.parseLocation(b.location)
  const parts = [b.address || b.custAddress, loc && loc.subDt, loc && loc.district, loc && loc.province, b.zipcode]
  const text = parts.filter(Boolean).join(' ').trim()
  if (text) return text

  return routeTargetFor(b)
}

// Returns { url, waypoints, truncated, dropped } -- or null when the round has
// no stop that can be navigated to at all.
function mapUrlForRoute (route) {
  if (!route || !route.stops || !route.stops.length) return null

  // The point the planner placed the stop at, when it has one. The planner
  // runs in a worker that knows every province (geo.addPoints); this runs on
  // the page, which does not, and re-resolving here dropped every far ตำบล
  // from an out-of-area route's map.
  const pointOf = s => s.job.point && geo.isPoint(s.job.point) && s.job.point.precision
    ? s.job.point.lat + ',' + s.job.point.lng
    : routeTargetFor(s.job.booking)
  const targets = route.stops
    .map(pointOf)
    .filter(Boolean)

  if (!targets.length) return null

  const kept = targets.slice(0, MAX_WAYPOINTS)
  const depot = geo.DEPOT.lat + ',' + geo.DEPOT.lng

  // Origin and destination are both the depot: the round starts and ends there,
  // and showing the return leg is half the point -- it is the leg that decides
  // whether the day finishes before the evening rush.
  const url = 'https://www.google.com/maps/dir/?api=1' +
    '&travelmode=driving' +
    '&origin=' + encodeURIComponent(depot) +
    '&destination=' + encodeURIComponent(depot) +
    '&waypoints=' + kept.map(encodeURIComponent).join('%7C')

  return {
    url: url,
    waypoints: kept.length,
    truncated: targets.length > MAX_WAYPOINTS,
    dropped: Math.max(0, targets.length - MAX_WAYPOINTS)
  }
}

// One stop on its own, for the row-level map icon. Same target rule.
// One URL builder, two shapes. A planned stop wraps its booking in `job`; a
// set-aside booking is the booking, because nothing routed it. The นอกพื้นที่
// block needs the same map button the routed rows have -- it is the round
// nobody sequenced, so it is the one where seeing the point matters most.
function mapUrlForBooking (booking) {
  const target = navTargetFor(booking)
  if (!target) return null
  return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(target)
}

function mapUrlForStop (stop) {
  return stop && stop.job ? mapUrlForBooking(stop.job.booking) : null
}

// What to write back onto the bookingSlot document, so จองวันส่งสินค้า can show
// the customer which car is coming without reading the booking collection.
function toSlotPatches (plan, meta) {
  const m = meta || {}
  const patches = []
  plan.routes.forEach(route => {
    route.stops.forEach((stop, index) => {
      (stop.job.bookings || [stop.job.booking]).forEach(b => {
        const id = b && b.id
        if (!id) return
        patches.push({
          id: id,
          patch: {
            regNum: route.car.regNum,
            stopOrder: index + 1,
            etaStart: travelTime.toClock(stop.startAt),
            planVersion: m.planVersion || ''
          }
        })
      })
    })
  })
  return patches
}

module.exports = {
  routeDayHolds,
  carriedPlates,
  DEFAULTS,
  UNASSIGNED_TH,
  config,
  slotWindow,
  toJob,
  visitKeyFor,
  groupVisits,
  toVisitJob,
  shareMinutes,
  simulate,
  simulateAt,
  simulateTwoDay,
  insertionCost,
  difficulty,
  improve,
  searchWider,
  planDay,
  planOptions,
  moveChoices,
  moveLocal,
  pairPlans,
  PIN_REASON_TH,
  describePlan,
  slotForStop,
  MAX_WAYPOINTS,
  navTargetFor,
  routeTargetFor,
  mapUrlForRoute,
  mapUrlForStop,
  bookingDocFrom,
  toCarSlot,
  mapUrlForBooking,
  groupSetAside,
  planTrips,
  tripPasses,
  bestTwoDay,
  tripForCar,
  retimeTripSplit,
  visitPinned,
  sentPinPatches,
  sentPlates,
  unsendPlan,
  dispatchedConflicts,
  TRIP_REASON_TH,
  routedFields,
  tripStopFields,
  toBookingDocs,
  toSlotPatches
}
